/*
  ATHLO+ — "Create Your Athlete" avatar  →  window.HubAvatar   (v3, jointed rig)
  ===========================================================================
  A layered SVG athlete. The HEAD comes from Open Peeps (CC0, Pablo Stanley —
  see hub-peeps.js); the BODY is ours, drawn in the same hand-inked style so the
  two read as one illustration.

  WHY THE BODY IS OURS. Open Peeps ships its own bodies, but each one is two
  monolithic paths with the clothing fused into the silhouette — one ink path,
  one fill path. Using them would have cost us the whole wardrobe: no separate
  top / bottoms / shoes, no sport kit, one fabric colour for the entire outfit.
  So we take the head stack (34 hairstyles, 18 expressions, 16 beards, 8
  eyewear — where the character actually lives) and draw the athlete underneath.

  hub-peeps.js is OPTIONAL. Without it we fall back to a simple drawn head, so
  a failed/slow load degrades instead of breaking.

  ---------------------------------------------------------------------------
  WHAT v3 CHANGED, AND THE MEASUREMENT THAT FORCED IT (2026-08-07)
  ---------------------------------------------------------------------------
  v2 failed the silhouette test — the standard check for any character system.
  Flattened to solid black, six members across four builds, four sports and
  four garment lengths came out as ONE shape. Scored as Jaccard distance on a
  48px mask (the size an avatar is actually seen at on a leaderboard row)
  against the no-sport base, v2 averaged 0.2%, and running / cycling / swimming
  / yoga / basketball each scored EXACTLY 0.0% — choosing a sport changed not
  one pixel.

  The cause was structural, not artistic: every sport kit differentiated by
  FILL inside a fixed outline (a collar cut, a stripe, a chest bib), and
  patternSVG() is clipped to the torso path. None of that survives a flatten,
  and none of it survives being 80px tall on a phone either.

  So v3 changes the four things that actually move an outline:

    1. JOINTED RIG. v2 baked the pose into path strings, so the pose was not a
       thing that could vary. The skeleton is now DATA — named shoulder/elbow/
       hand, hip/knee/ankle joints — and every path is generated from it.
    2. STANCES (5, free to everyone). A symmetric A-pose reads as a corporate
       mannequin; asymmetry is what reads as an athlete.
    3. HANDS, FEET AND HELD PROPS (15, free to everyone). v2 arms were rounded
       stroke caps ending mid-thigh, so the character could not hold anything —
       the hard ceiling on the whole cosmetic economy. A held object breaks the
       outline OUTSIDE the body mass, which is the only place a flattened shape
       has room to differ.
    4. DIE-CUT RIM. v2 inked at #11151B on a #151A22 plate — the outline was
       the same value as the background, so darker skin tones lost the arms,
       legs and shoes entirely. Every silhouette-defining primitive is now
       re-stroked in a plate-contrasting colour behind the figure.

  v3 scores 5.1% mean on the same test, and no sport scores under 3%.

  THE RIM IS DRAWN, NOT AN SVG FILTER. feMorphology would be one line and is
  rejected for two reasons. First, _verify/avatar/ rasterises the silhouette
  through an <img> onto a canvas to score the gate, and filters do not survive
  that path reliably — a gate that cannot measure the thing it guards is not a
  gate. Second, a filter cannot be expressed as a primitive, and the whole
  design here is that ONE primitive list drives all three passes.
  (An earlier version of this note claimed hub-athlete-card.js rasterises the
  avatar for the share PNG. It does not — that canvas draws a user-uploaded
  PHOTO, and the avatar is only ever inline SVG in the DOM. Corrected rather
  than deleted, because "it must survive canvas" is still true of the gate.)

  ONE GEOMETRY, THREE PASSES. bodyPrims() returns a flat ordered list, and the
  same list draws the figure, the rim and the black silhouette. That is what
  makes the silhouette a testable GATE (_verify/avatar/) rather than an
  opinion. Decorative fills — collar, fabric pattern, kit furniture,
  accessories — are drawn separately AFTER the body and are deliberately
  excluded from the rim and silhouette passes: they sit inside the outline, so
  they contribute nothing to either and would only cost markup.

  API (unchanged from v2 — every existing caller keeps working):
    HubAvatar.defaultKit()                        -> starter kit object
    HubAvatar.svg(kit, {size, crop, bg, architect, rim}) -> SVG markup
    HubAvatar.renderInto(el, kit, size, opts)     -> draw into an element
    HubAvatar.builder(host, {kit, earnedIds, onChange, badges}) -> picker UI
    HubAvatar.parts / .sports / .stances / .props / .lockOf(cat,id)
    HubAvatar.archetypeKit(kit, archetype)        -> kit dressed for an archetype
    HubAvatar.migrate(kit)                        -> v1/v2 kit -> v3 kit
    HubAvatar.silhouette(kit, size)               -> black-shape SVG (the gate)

  Kit shape:
    { skin, build, stance, hair, hairColor, face, beard, glasses,
      top, topColor, sport, prop, bottom, bottomColor, shoe, accessory, frame }

  COORDINATE CONTRACT (do not change casually — hub-peeps.js is drawn to it):
    Full figure lives in a 704-wide space. Head stack occupies y 60..567
    (jaw bottom ~567). Neck 567..660. Shoulders 660. Body centre x = 380.
    Everything BELOW the shoulder is scaled by the build's height factor, so
    a taller athlete grows out of the same head instead of scaling into a
    giant and dragging the Open Peeps anchor with it.
*/
(function () {
  'use strict';

  // ---- palettes -----------------------------------------------------------
  // Skin follows the Monk Skin Tone scale (10 steps) rather than a hand-picked
  // ramp — Fitzpatrick, the usual default, under-represents darker shades.
  var SKIN = ['#F6EDE4', '#F3E7DB', '#F7EAD0', '#EADABA', '#D7BD96', '#A07E56', '#825C43', '#604134', '#3A312A', '#292420'];
  var HAIRC = ['#1A1A1A', '#3B2417', '#6B4423', '#A9743B', '#D8B36B', '#E8E2D0', '#9AA0A6', '#B7FF00', '#2533F5', '#FF4D6D', '#7A4DFF', '#15C7A8'];
  // Kit black is lifted to a charcoal: true #11151B is the same value as the ink
  // outline, so black kit on the dark plate rendered as an invisible silhouette.
  var CLOTH = ['#B7FF00', '#2533F5', '#232A34', '#FFFFFF', '#FF4D6D', '#FF8A3D', '#15C7A8', '#7A4DFF', '#FFD84D', '#E5E7EB'];
  var SHOEC = ['#11151B', '#FFFFFF', '#B7FF00', '#2533F5', '#FF4D6D', '#9AA0A6'];

  /* Border / plate palettes (2026-08-07, owner ask: "we need to be able to
     change the border too"). Three separate borders exist on this figure and
     all three are now member-controlled:
       frameColor  the ring around the CARD
       plateColor  the card BACKGROUND behind the athlete
       rimColor    the die-cut OUTLINE tracing the athlete
     Every one defaults to 'auto', which means "whatever the frame preset
     already decided" — so an existing kit, which has none of these keys, is
     pixel-identical after the upgrade.
     The colour is free; the PRESETS (Champion, Holo) stay achievement-locked,
     because their treatment is the reward, not the hue. */
  var FRAMEC = ['#2A2F36', '#B7FF00', '#2533F5', '#FFD84D', '#FF4D6D', '#FF8A3D', '#15C7A8', '#7A4DFF', '#FFFFFF', '#9AA0A6', '#E11D48', '#06B6D4'];
  var PLATEC = ['#151A22', '#0B0E12', '#232A34', '#1E2A44', '#2A1E44', '#3A2A16', '#14342A', '#3A3F46', '#42121F', '#1A1030'];
  var RIMC   = ['#E6ECF2', '#FFFFFF', '#11151B', '#B7FF00', '#9AA0A6', '#FFD84D', '#2533F5', '#FF4D6D', '#15C7A8', '#7A4DFF'];
  // The 'auto' chip is a split swatch so it reads as "not a colour" at a glance.
  // Its `color` is a CSS gradient, which is fine for a chip background and is
  // kept OUT of the SVG by pickColor()'s hex guard.
  var AUTO_CHIP = { id: 'auto', label: 'Auto', color: 'linear-gradient(135deg,#3A424D 0 50%,#8A9099 50%)' };
  function swAuto(list) { return [AUTO_CHIP].concat(sw(list)); }

  function sw(list) { return list.map(function (c, i) { return { id: String(i), color: c }; }); }

  // Declared up here rather than with the frames: pickColor() guards every
  // member-chosen colour with it, and that runs long before frameSVG does.
  var HEXOK = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
  var INK = '#11151B';          // the hand-inked outline, matched to Open Peeps
  var LW = 15;                  // line weight at 704-wide scale
  var RIM = 13;                 // die-cut rim half-width
  var RIM_COLOR = '#E6ECF2';    // neutral (owner call 2026-08-07). Kept plate-agnostic
                                // on purpose: an accent rim ties the figure to one
                                // frame, and the avatar renders on four different
                                // surfaces with four different backgrounds.

  // ---- body builds --------------------------------------------------------
  // Gender-neutral. Every measurement the body paths need lives here, so adding
  // a build is a data change, not a drawing job.
  // Shoulders are sized against the HEAD, not against each other: an Open Peeps
  // head is ~370-500 units wide, so anything under ~340 across the shoulders
  // reads as a bobblehead rather than an athlete.
  // `ht` is v3. v2 had no height at all — every member was exactly 1350 units
  // tall, and the builds varied only in width under a fixed-size head, which is
  // why they read as near-identical.
  var BUILDS = [
    { id: 'lean',     label: 'Lean',     sh: 160, chest: 148, waist: 116, hip: 132, arm: 42, leg: 58, ht: 1.06 },
    { id: 'athletic', label: 'Athletic', sh: 196, chest: 186, waist: 144, hip: 156, arm: 56, leg: 74, ht: 1.00 },
    { id: 'strong',   label: 'Strong',   sh: 236, chest: 226, waist: 178, hip: 184, arm: 76, leg: 92, ht: 0.97 },
    { id: 'fuller',   label: 'Fuller',   sh: 214, chest: 224, waist: 212, hip: 216, arm: 70, leg: 98, ht: 0.99 },
    { id: 'compact',  label: 'Compact',  sh: 178, chest: 172, waist: 140, hip: 152, arm: 52, leg: 70, ht: 0.88 },
    { id: 'tall',     label: 'Tall',     sh: 190, chest: 176, waist: 138, hip: 150, arm: 54, leg: 72, ht: 1.14 }
  ];
  function buildOf(id) {
    for (var i = 0; i < BUILDS.length; i++) if (BUILDS[i].id === id) return BUILDS[i];
    return BUILDS[1];
  }

  // Skeleton constants (see coordinate contract above). The 0-suffixed ones are
  // the height-1.0 values; skel() scales them.
  var CX = 380, NECK_TOP = 545, SHOULDER = 660,
      CHEST0 = 745, WAIST0 = 900, HIP0 = 952, GROUND0 = 1350;
  // How far above the waist a crop top hems. Shared so the crop shirt fill and
  // the fabric-pattern clip agree — if they drift, a sport pattern paints past
  // the shirt onto the bare midriff (the exact bug this constant closes).
  var CROP_DROP = 108;

  function skel(b) {
    var ht = b.ht || 1;
    function y(v) { return SHOULDER + (v - SHOULDER) * ht; }
    return {
      b: b, ht: ht,
      CHEST: y(CHEST0), WAIST: y(WAIST0), HIP: y(HIP0), GROUND: y(GROUND0),
      armLen: 372 * ht, legLen: (GROUND0 - HIP0 - 50) * ht
    };
  }

  /* ---- STANCES ------------------------------------------------------------
     A stance returns joint offsets FROM the joint each limb hangs off, in the
     704-wide space. Written as functions of the skeleton so a stance that has
     to land a hand on the waist (hips) or clear a wide chest (crossed) stays
     correct on every build instead of only looking right on 'athletic'.

     arms[i] / legs[i] are indexed 0 = viewer-left, 1 = viewer-right.
     `fd` is the direction the foot points, so a shoe can never face the wrong
     way when a stance widens the stance.

     Free to everyone (owner call 2026-08-07). Deliberately NOT gated: most
     members would keep the mannequin pose, which is the exact complaint this
     work started from.
  -------------------------------------------------------------------------- */
  var STANCES = [
    { id: 'ready', label: 'Ready', grip: 'side',
      // Contrapposto — weight on one leg. The cheapest possible fix for the
      // "static symmetric mannequin" read, and the reason it is the default.
      f: function (s) {
        var L = s.armLen, G = s.legLen;
        return {
          arms: [{ e: [-26, L * 0.48], h: [-40, L] }, { e: [32, L * 0.47], h: [54, L * 0.99] }],
          legs: [{ k: [-4, G * 0.5], a: [-12, G], fd: -1 }, { k: [16, G * 0.49], a: [34, G * 0.99], fd: 1 }],
          tilt: -2, lean: -6, hipTilt: 8
        };
      } },
    { id: 'stride', label: 'Mid-stride', grip: 'side',
      // One knee driven up, opposite arm cocked. The only stance that reads as
      // MOTION in a flattened silhouette.
      f: function (s) {
        var L = s.armLen, G = s.legLen;
        return {
          arms: [{ e: [-58, L * 0.42], h: [-2, L * 0.10] }, { e: [58, L * 0.44], h: [72, L * 0.92] }],
          legs: [{ k: [-46, G * 0.34], a: [-86, G * 0.66], fd: -1 }, { k: [26, G * 0.52], a: [62, G * 0.99], fd: 1 }],
          tilt: 4, lean: 10, hipTilt: -6
        };
      } },
    { id: 'hips', label: 'Hands on hips', grip: 'none',
      // Elbows out cut two negative-space triangles — the classic way to buy
      // silhouette without adding any art.
      f: function (s) {
        var b = s.b, L = s.armLen;
        // Land the hand ON the waist whatever the build is, rather than at a
        // hardcoded x that only fits one torso.
        var dx = (b.sh - b.waist) - 10;
        return {
          arms: [{ e: [-b.sh * 0.62 - 30, L * 0.44], h: [dx, L * 0.70] },
                 { e: [b.sh * 0.62 + 30, L * 0.44], h: [-dx, L * 0.70] }],
          legs: [{ k: [-8, s.legLen * 0.5], a: [-26, s.legLen], fd: -1 },
                 { k: [8, s.legLen * 0.5], a: [26, s.legLen], fd: 1 }],
          tilt: 0, lean: 0, hipTilt: 0
        };
      } },
    { id: 'crossed', label: 'Arms crossed', grip: 'none',
      f: function (s) {
        var b = s.b, L = s.armLen;
        return {
          arms: [{ e: [-b.sh * 0.52 - 34, L * 0.42], h: [b.chest * 0.42, L * 0.60] },
                 { e: [b.sh * 0.52 + 34, L * 0.44], h: [-b.chest * 0.42, L * 0.66] }],
          legs: [{ k: [-10, s.legLen * 0.5], a: [-22, s.legLen], fd: -1 },
                 { k: [12, s.legLen * 0.5], a: [30, s.legLen * 0.99], fd: 1 }],
          tilt: -3, lean: -4, hipTilt: 4
        };
      } },
    { id: 'victory', label: 'Finish line', grip: 'overhead',
      f: function (s) {
        var L = s.armLen;
        return {
          arms: [{ e: [-L * 0.17, L * 0.10], h: [-L * 0.21, -L * 0.55] },
                 { e: [L * 0.17, L * 0.10], h: [L * 0.21, -L * 0.55] }],
          legs: [{ k: [-16, s.legLen * 0.5], a: [-40, s.legLen], fd: -1 },
                 { k: [16, s.legLen * 0.5], a: [40, s.legLen], fd: 1 }],
          tilt: 0, lean: 0, hipTilt: 0
        };
      } }
  ];
  function stanceOf(id) {
    for (var i = 0; i < STANCES.length; i++) if (STANCES[i].id === id) return STANCES[i];
    return STANCES[0];
  }
  // Both hands are occupied in these two. That drives two separate things: the
  // arms are drawn IN FRONT of the torso (folded arms behind the chest simply
  // disappear), and a held prop is set down at the feet instead. Renamed from
  // STANCE_NO_PROP once "no prop" stopped being true — the item is relocated
  // now, not deleted.
  var STANCE_HANDS_BUSY = { hips: 1, crossed: 1 };

  // ---- the sport arsenal --------------------------------------------------
  // Each sport is a COMPOSITION: a collar shape, a fabric pattern, optional kit
  // furniture (race bib, harness, belt…) and — new in v3 — a default held prop.
  // The first three are fills and, as the silhouette measurement showed, do not
  // survive a flatten on their own. The prop is what makes a netballer and a
  // lifter different SHAPES.
  var SPORTS = [
    { id: 'none',        label: 'None',            collar: 'crew',  pattern: 'none' },
    { id: 'running',     label: 'Running',         collar: 'vee',   pattern: 'none',     bib: true,    accent: '#B7FF00', prop: 'bottle' },
    { id: 'cycling',     label: 'Cycling',         collar: 'zip',   pattern: 'vstripe',  accent: '#2533F5', prop: 'helmet' },
    { id: 'triathlon',   label: 'Triathlon',       collar: 'zip',   pattern: 'panel',    accent: '#FF4D6D', prop: 'helmet' },
    { id: 'swimming',    label: 'Swimming',        collar: 'crew',  pattern: 'diagonal', goggles: true, accent: '#15C7A8', prop: 'towel' },
    { id: 'hyrox',       label: 'Hyrox',           collar: 'vee',   pattern: 'none',     bib: true,    accent: '#FF8A3D', prop: 'kettlebell' },
    { id: 'strength',    label: 'Strength & Lift', collar: 'tank',  pattern: 'none',     belt: true,   accent: '#FFD84D', prop: 'dumbbell' },
    { id: 'climbing',    label: 'Climbing',        collar: 'crew',  pattern: 'none',     harness: true, accent: '#7A4DFF', prop: 'skiprope' },
    { id: 'yoga',        label: 'Yoga',            collar: 'tank',  pattern: 'band',     accent: '#FF7AC6', prop: 'matroll' },
    { id: 'football',    label: 'Football',        collar: 'polo',  pattern: 'vstripe',  number: true, accent: '#22C55E', prop: 'football' },
    { id: 'basketball',  label: 'Basketball',      collar: 'tank',  pattern: 'none',     number: true, accent: '#F97316', prop: 'basketball' },
    { id: 'netball',     label: 'Netball',         collar: 'crew',  pattern: 'bibpanel', accent: '#E11D48', prop: 'netball' },
    { id: 'rugby',       label: 'Rugby',           collar: 'polo',  pattern: 'hoops',    accent: '#0EA5E9', prop: 'rugbyball' },
    { id: 'tennis',      label: 'Tennis',          collar: 'polo',  pattern: 'none',     accent: '#A3E635', prop: 'tennisracket' },
    { id: 'badminton',   label: 'Badminton',       collar: 'crew',  pattern: 'shoulder', accent: '#06B6D4', prop: 'badmintonracket' },
    { id: 'tabletennis', label: 'Table Tennis',    collar: 'polo',  pattern: 'shoulder', accent: '#EF4444', prop: 'paddle' },
    { id: 'padel',       label: 'Padel',           collar: 'polo',  pattern: 'band',     accent: '#8B5CF6', prop: 'padelracket' }
  ];
  function sportOf(id) {
    for (var i = 0; i < SPORTS.length; i++) if (SPORTS[i].id === id) return SPORTS[i];
    return SPORTS[0];
  }

  /* ---- PROPS --------------------------------------------------------------
     The category v2 could not have, because there were no hands to put one in.

     Each prop draws around its own origin (0,0) = the hand it is held in, and
     declares which hand and how far out to sit. `off` pushes it AWAY from the
     body centre: held tight against the thigh a ball is swallowed by the torso
     mass and buys almost no outline, which is the whole reason it exists.

     Held objects were drawn at "correct" scale first and read as trinkets, so
     the whole set is scaled up together by PROP_SCALE rather than one at a time.
     Free to everyone (owner call 2026-08-07) — no `lock` on any of them.
  -------------------------------------------------------------------------- */
  var PROP_SCALE = 1.34;
  function pa(d, fill) { return { d: d, fill: fill, w: 0, ink: true }; }
  function pf(d, fill) { return { d: d, fill: fill, w: 0, ink: false }; }
  function stk(d, w, col) { return { d: d, fill: 'none', w: w, col: col || INK, ink: false }; }
  function cir(x, y, r, fill) {
    return pa('M' + x + ' ' + y + 'm-' + r + ' 0a' + r + ' ' + r + ' 0 1 0 ' + (r * 2) + ' 0a' + r + ' ' + r + ' 0 1 0 -' + (r * 2) + ' 0', fill);
  }
  var PROPS = {
    none:            { label: 'None', empty: true },
    basketball:      { label: 'Basketball', hand: 1, hw: 78, gy: 108, off: [26, 26], d: function () {
      return [cir(0, 30, 78, '#F97316'), stk('M-78 30a78 78 0 0 0 156 0', 6), stk('M0 -48v156', 6),
              stk('M-58 -20c40 30 76 30 116 0', 6), stk('M-58 80c40-30 76-30 116 0', 6)]; } },
    football:        { label: 'Football', hand: 1, hw: 72, gy: 104, off: [24, 28], d: function () {
      return [cir(0, 32, 72, '#FFFFFF'), pf('M0 -22l32 24-12 38h-40l-12-38Z', INK), pf('M0 -94l-30 22 8 12 44 0 8-12Z', INK)]; } },
    rugbyball:       { label: 'Rugby ball', hand: 1, hw: 78, gy: 66, off: [26, 22], d: function () {
      return [pa('M0 -66c46 0 78 30 78 66s-32 66-78 66-78-30-78-66 32-66 78-66Z', '#8B5A2B'),
              stk('M-34 30h68', 8), stk('M-16 6v48', 8)]; } },
    netball:         { label: 'Netball', hand: 1, hw: 74, gy: 104, off: [26, 26], d: function () {
      return [cir(0, 30, 74, '#E11D48'), stk('M-74 30h148', 6), stk('M0 -44v148', 6)]; } },
    tennisracket:    { label: 'Tennis racket', hand: 1, hw: 82, gy: 154, d: function () {
      return [stk('M0 6l26 74', 22, '#232A34'), pa('M26 80m-56 0a56 74 0 1 0 112 0a56 74 0 1 0 -112 0', '#B7FF00'),
              stk('M-16 60h84', 4), stk('M-16 100h84', 4), stk('M6 26v108', 4), stk('M46 26v108', 4)]; } },
    badmintonracket: { label: 'Badminton racket', hand: 1, hw: 64, gy: 164, d: function () {
      return [stk('M0 6l22 92', 18, '#232A34'), pa('M22 104m-42 0a42 60 0 1 0 84 0a42 60 0 1 0 -84 0', '#06B6D4'),
              stk('M-14 88h72', 4), stk('M4 52v104', 4)]; } },
    padelracket:     { label: 'Padel bat', hand: 1, hw: 72, gy: 124, d: function () {
      return [stk('M0 6l20 56', 22, '#232A34'), pa('M20 62m-52 0a52 62 0 1 0 104 0a52 62 0 1 0 -104 0', '#8B5CF6'),
              cir(-2, 40, 11, INK), cir(40, 40, 11, INK), cir(20, 72, 11, INK), cir(-14, 84, 11, INK), cir(54, 84, 11, INK)]; } },
    // Blade enlarged 2026-08-07: at the original 46×50 the bat scored 3.0% on the
    // silhouette gate — technically a pass, but sitting on the floor.
    paddle:          { label: 'Table-tennis bat', hand: 1, hw: 72, gy: 122, d: function () {
      return [stk('M0 4l16 54', 22, '#8B4513'), pa('M16 60m-56 0a56 62 0 1 0 112 0a56 62 0 1 0 -112 0', '#EF4444')]; } },
    dumbbell:        { label: 'Dumbbell', hand: 1, hw: 84, gy: 66, off: [10, 6], d: function () {
      return [stk('M-52 26h104', 16, '#9AA0A6'), pa('M-84 -14h34v80h-34Z', '#232A34'), pa('M50 -14h34v80h-34Z', '#232A34')]; } },
    kettlebell:      { label: 'Kettlebell', hand: 1, hw: 62, gy: 154, off: [18, 10], d: function () {
      return [stk('M-34 10C-34-32 34-32 34 10', 18, '#3A424D'), pa('M0 96m-62 0a62 58 0 1 0 124 0a62 58 0 1 0 -124 0', '#3A424D')]; } },
    matroll:         { label: 'Yoga mat', hand: 1, hw: 24, gy: 90, off: [22, 0], d: function () {
      return [pa('M-24 -70h48v160h-48Z', '#FF7AC6'), pa('M0 -70m-24 0a24 12 0 1 0 48 0a24 12 0 1 0 -48 0', '#FFA8DC')]; } },
    // Grown 2026-08-07: giving the hands thumbs enlarged the base figure just
    // enough that the old bottle fell to 2.8% and the silhouette gate rejected
    // running. A drinks bottle is the right object for a runner, so the bottle
    // got bigger rather than the sport getting a different prop.
    bottle:          { label: 'Bottle', hand: 1, hw: 34, gy: 132, d: function () {
      return [pa('M-34 -8h68v140h-68Z', '#B7FF00'), pa('M-18 -60h36v52h-36Z', '#B7FF00'), pa('M-22 -78h44v22h-44Z', INK)]; } },
    helmet:          { label: 'Helmet', hand: 1, hw: 66, gy: 42, off: [22, 14], d: function () {
      return [pa('M-66 42a66 56 0 0 1 132 0Z', '#2533F5'), stk('M-40 12l16 30', 7), stk('M0 -8v50', 7), stk('M40 12l-16 30', 7)]; } },
    towel:           { label: 'Towel', hand: 0, hw: 30, gy: 140, off: [16, 0], d: function () {
      return [pa('M-30 -10h60l-10 150h-40Z', '#E5E7EB'), stk('M-26 26h52', 6), stk('M-24 118h48', 6)]; } },
    // The one cosmetic the silhouette gate actually REJECTED (2.8% against a 3%
    // floor). A 9-wide rope is a hairline at 704-wide scale, so it vanished the
    // moment the figure was flattened. Thicker cord, wider sweep — the fix is to
    // make the item read, not to lower the floor it failed.
    skiprope:        { label: 'Skipping rope', hand: 1, hw: 78, ghw: 92, gy: 96, d: function () {
      return [pa('M-16 -6h32v88h-32Z', '#FF4D6D'), stk('M16 52C78 140 62 258 -26 236', 16)];
    // A held rope hangs; a set-down rope is a COIL. Reusing the held drawing here
    // would stand a limp rope on its end with the handle floating at knee height,
    // which is the kind of detail that makes the whole figure look broken.
    }, gd: function () {
      return [stk('M0 60m-92 0a92 36 0 1 0 184 0a92 36 0 1 0 -184 0', 16, '#FF4D6D'),
              stk('M0 46m-58 0a58 24 0 1 0 116 0a58 24 0 1 0 -116 0', 16, '#FF4D6D'),
              pa('M-22 -8h44v54h-44Z', '#FF4D6D')]; } }
  };
  function propOf(id) { return PROPS[id] || null; }

  // ---- catalogue ----------------------------------------------------------
  // `lock` = the achievement id that unlocks the item (see hub-profile.js).
  var parts = {
    skin:        sw(SKIN),
    build:       BUILDS.map(function (b) { return { id: b.id, label: b.label }; }),
    stance:      STANCES.map(function (s) { return { id: s.id, label: s.label }; }),
    hair:        [],                       // filled from HubPeeps at init
    hairColor:   sw(HAIRC),
    face:        [],
    beard:       [],
    glasses:     [],
    top:         [{ id: 'singlet', label: 'Singlet' }, { id: 'tee', label: 'Tee' }, { id: 'long', label: 'Long sleeve' }, { id: 'hoodie', label: 'Hoodie' }, { id: 'crop', label: 'Crop' }, { id: 'jacket', label: 'Jacket', lock: 'og' }],
    topColor:    sw(CLOTH),
    sport:       SPORTS.map(function (s) { return { id: s.id, label: s.label }; }),
    // 'auto' first so the default reads as "whatever my sport carries", which is
    // what a member expects before they have thought about props at all.
    prop:        [{ id: 'auto', label: 'Match my sport' }].concat(
                   Object.keys(PROPS).map(function (id) { return { id: id, label: PROPS[id].label }; })),
    bottom:      [{ id: 'shorts', label: 'Shorts' }, { id: 'tights', label: 'Tights' }, { id: 'joggers', label: 'Joggers' }, { id: 'skort', label: 'Skort' }],
    bottomColor: sw(CLOTH),
    shoe:        sw(SHOEC),
    accessory:   [{ id: 'none', label: 'None' }, { id: 'vest', label: 'Hydration vest' }, { id: 'belt', label: 'Lifting belt' }, { id: 'ktape', label: 'K-tape' }, { id: 'gloves', label: 'Mitts' }, { id: 'medal', label: 'Medal', lock: 'race' }, { id: 'wristband', label: 'Starter band', lock: 'profile100' }],
    frame:       [{ id: 'none', label: 'None' }, { id: 'ring', label: 'Ring' }, { id: 'lime', label: 'Lime' }, { id: 'grad', label: 'Gradient' }, { id: 'laurel', label: 'Champion', lock: 'join10' }, { id: 'holo', label: 'Holo', lock: 'mythic' }],
    frameColor:  swAuto(FRAMEC),
    plateColor:  swAuto(PLATEC),
    rimColor:    swAuto(RIMC)
  };

  // Head parts come from hub-peeps.js when it is present. Locked hairstyles are
  // declared here so the lock lives with the catalogue, not the art file.
  var HAIR_LOCKS = { mohawk: 'mythic' };
  function initPeeps() {
    var P = window.HubPeeps;
    if (!P) { parts.hair = [{ id: 'short1', label: 'Short' }]; parts.face = [{ id: 'calm', label: 'Calm' }]; parts.beard = [{ id: 'none', label: 'None' }]; parts.glasses = [{ id: 'none', label: 'None' }]; return false; }
    parts.hair = P.list('hair').map(function (h) { if (HAIR_LOCKS[h.id]) h.lock = HAIR_LOCKS[h.id]; return h; });
    parts.face = P.list('face');
    parts.beard = [{ id: 'none', label: 'None' }].concat(P.list('beard'));
    parts.glasses = [{ id: 'none', label: 'None' }].concat(P.list('glasses'));
    return true;
  }

  function lockOf(cat, id) {
    var arr = parts[cat] || [];
    for (var i = 0; i < arr.length; i++) if (arr[i].id === id) return arr[i].lock || null;
    return null;
  }
  function colorOf(cat, id) {
    var arr = parts[cat] || [];
    for (var i = 0; i < arr.length; i++) if (arr[i].id === id) return arr[i].color;
    return null;
  }
  /* Resolve a member-chosen colour, or fall back. The hex guard is load-bearing
     in two ways: the 'auto' chip's `color` is a CSS GRADIENT (great in a chip
     background, catastrophic as an SVG fill), and a saved kit is whatever JSON
     a profile blob happens to contain — including one written by a future
     version, or a corrupted one. Anything that is not a plain hex resolves to
     the fallback instead of painting garbage. */
  /* Letting members choose the outline AND the plate re-opens the exact bug the
     die-cut rim was added to close: an outline the same value as the background
     makes the figure disappear, worst on darker skin. This is not blocked — it
     is their avatar — but the builder says so, because somebody picking two
     dark colours in a row has almost certainly not noticed yet. */
  function lum(hex) {
    var h = String(hex).replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var v = parseInt(h, 16);
    if (isNaN(v)) return 0;
    var r = (v >> 16 & 255) / 255, g = (v >> 8 & 255) / 255, b = (v & 255) / 255;
    function lin(c) { return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  }
  function lowContrast(a, b) { return Math.abs(lum(a) - lum(b)) < 0.06; }

  function pickColor(cat, id, fallback) {
    if (!id || id === 'auto') return fallback;
    var c = colorOf(cat, id);
    return (c && HEXOK.test(c)) ? c : fallback;
  }

  function labelOf(cat, id) {
    var arr = parts[cat] || [];
    for (var i = 0; i < arr.length; i++) if (arr[i].id === id) return arr[i].label || '';
    return '';
  }
  function has(cat, id) {
    var arr = parts[cat] || [];
    for (var i = 0; i < arr.length; i++) if (arr[i].id === id) return true;
    return false;
  }

  function defaultKit() {
    // __v2 / __v3 are stamped here on purpose. migrate() reads `!k.__v2` as
    // "this is a legacy v1 kit" and remaps skin 0-5 up the old 6-step ramp.
    // norm() only copies keys that exist in defaultKit(), so leaving the flags
    // out meant norm STRIPPED them every pass — and the v1 skin remap then
    // re-fired on every single render, silently darkening skins 0-5 (a chosen
    // swatch never matched the figure, and true v1 kits drifted 2->4->7 across
    // saves). With the flags in the default key set, norm carries them through,
    // migrate is idempotent, and a fresh v3 kit is never mistaken for v1.
    return { skin: '3', build: 'athletic', stance: 'ready', hair: 'short1', hairColor: '0', face: 'calm', beard: 'none', glasses: 'none',
             top: 'singlet', topColor: '0', sport: 'none', prop: 'auto', bottom: 'shorts', bottomColor: '2', shoe: '0', accessory: 'none', frame: 'ring', frameColor: 'auto', plateColor: 'auto', rimColor: 'auto',
             __v2: 1, __v3: 1 };
  }

  // ---- v1 -> v2 -> v3 migration -------------------------------------------
  // Members already have saved kits. Old ids must land somewhere sensible or a
  // year of saved avatars silently resets to the default.
  //
  // v2 -> v3 needs no id remapping at all: the two new categories (stance, prop)
  // simply do not exist on a v2 kit, and norm() fills them from defaultKit(),
  // which lands every existing member on Ready + Match-my-sport. That is the
  // intended outcome and is asserted in _verify/avatar/.
  var HAIR_MIGRATE = { bald: 'noHair1', short: 'short1', curls: 'longCurly', afro: 'afro', long: 'long', bun: 'bun', pony: 'bun2', mohawk: 'mohawk' };
  // v1 kept cap / headband / shades in `accessory`; from v2 on, headwear is a
  // hairstyle and eyewear is its own category, so those three move rather than vanish.
  var ACC_MIGRATE = { cap: { hair: 'hatHip' }, band: { hair: 'hatBeanie' }, shades: { glasses: 'sunglasses' }, crown: { frame: 'laurel' } };
  function migrate(kit) {
    if (!kit) return null;
    var k = {}; for (var p in kit) k[p] = kit[p];
    if (k.hair && HAIR_MIGRATE[k.hair]) k.hair = HAIR_MIGRATE[k.hair];
    if (k.accessory && ACC_MIGRATE[k.accessory]) {
      var m = ACC_MIGRATE[k.accessory];
      if (m.hair) k.hair = m.hair;
      if (m.glasses) k.glasses = m.glasses;
      if (m.frame) k.frame = m.frame;
      k.accessory = 'none';
    }
    // v1 skin was a 6-step ramp; v2 on is the 10-step Monk scale.
    if (k.skin != null && !k.__v2) {
      var old = parseInt(k.skin, 10);
      if (!isNaN(old) && old <= 5) k.skin = String([1, 3, 4, 6, 7, 9][old]);
    }
    delete k.eyes;                       // v1 eye-colour category is gone (Peeps faces carry the eyes)
    k.__v2 = 1;
    k.__v3 = 1;
    return k;
  }

  function norm(kit) {
    var d = defaultKit();
    kit = migrate(kit) || {};
    var o = {};
    for (var k in d) o[k] = kit[k] != null ? kit[k] : d[k];
    // Guard against ids that no longer exist (a renamed part must never render blank).
    if (!has('hair', o.hair)) o.hair = (parts.hair[0] || {}).id || 'short1';
    if (!has('face', o.face)) o.face = (parts.face[0] || {}).id || 'calm';
    if (!has('beard', o.beard)) o.beard = 'none';
    if (!has('glasses', o.glasses)) o.glasses = 'none';
    if (!has('build', o.build)) o.build = 'athletic';
    if (!has('stance', o.stance)) o.stance = 'ready';
    if (o.prop !== 'auto' && !PROPS[o.prop]) o.prop = 'auto';
    // A colour id that no longer exists must fall back to 'auto', not to a
    // blank fill — same rule the part categories follow.
    ['frameColor', 'plateColor', 'rimColor'].forEach(function (c) {
      if (o[c] !== 'auto' && !has(c, o[c])) o[c] = 'auto';
    });
    return o;
  }

  // ---- starter looks ------------------------------------------------------
  var PRESETS = [
    { name: 'Lime Runner',   kit: { skin: '2', build: 'lean',     stance: 'stride',  hair: 'short1',   hairColor: '0', face: 'driven',   beard: 'none', glasses: 'none',        top: 'singlet', topColor: '0', sport: 'running',   prop: 'auto', bottom: 'shorts',  bottomColor: '2', shoe: '0', accessory: 'none',      frame: 'ring' } },
    { name: 'Trail Head',    kit: { skin: '5', build: 'athletic', stance: 'ready',   hair: 'bun',      hairColor: '1', face: 'calm',     beard: 'none', glasses: 'sunglasses2', top: 'tee',     topColor: '1', sport: 'none',      prop: 'bottle', bottom: 'tights',  bottomColor: '2', shoe: '3', accessory: 'vest',      frame: 'ring' } },
    { name: 'Platform Lift', kit: { skin: '7', build: 'strong',   stance: 'hips',    hair: 'shaved1',  hairColor: '0', face: 'serious',  beard: 'full', glasses: 'none',        top: 'singlet', topColor: '2', sport: 'strength',  prop: 'auto', bottom: 'shorts',  bottomColor: '2', shoe: '1', accessory: 'belt',      frame: 'lime' } },
    { name: 'Night Hyrox',   kit: { skin: '8', build: 'athletic', stance: 'stride',  hair: 'cornrows', hairColor: '0', face: 'veryAngry', beard: 'none', glasses: 'none',       top: 'tee',     topColor: '2', sport: 'hyrox',     prop: 'auto', bottom: 'joggers', bottomColor: '2', shoe: '2', accessory: 'ktape',     frame: 'grad' } },
    { name: 'Lane Four',     kit: { skin: '1', build: 'lean',     stance: 'crossed', hair: 'hatBeanie', hairColor: '5', face: 'smileBig', beard: 'none', glasses: 'none',       top: 'crop',    topColor: '6', sport: 'swimming',  prop: 'auto', bottom: 'tights',  bottomColor: '2', shoe: '1', accessory: 'none',      frame: 'ring' } },
    { name: 'Court Violet',  kit: { skin: '6', build: 'fuller',   stance: 'victory', hair: 'twists',   hairColor: '0', face: 'cheeky',   beard: 'none', glasses: 'none',        top: 'tee',     topColor: '7', sport: 'basketball', prop: 'auto', bottom: 'shorts', bottomColor: '2', shoe: '2', accessory: 'wristband', frame: 'lime' } }
  ];
  // Starter looks are authored on the 10-step Monk scale, so they are v3 kits,
  // not legacy v1 ramps. Stamp them so migrate() never remaps their skin — the
  // thumbnails in the picker render svg(p.kit) directly, bypassing any other
  // stamping, and an unstamped preset showed the wrong skin tone in its chip.
  PRESETS.forEach(function (p) { p.kit.__v2 = 1; p.kit.__v3 = 1; });
  function presets() { return PRESETS.map(function (p) { return { name: p.name, kit: assign({}, p.kit) }; }); }

  /* ---- SAVED LOOKS (loadouts) --------------------------------------------------------------
     Without them the avatar is a one-time dress-up: the cost of trying the race kit is losing
     the everyday one, so most members build once and never touch it again.

     Always EXACTLY three entries. A shorter array from an older profile is padded and a longer
     one is truncated, so every caller can index 0..2 without guarding — and a corrupt slot
     becomes an empty slot rather than an exception, because this runs against whatever JSON a
     profile blob happens to contain, including one written by a future version.
  ------------------------------------------------------------------------------------------- */
  var LOADOUT_SLOTS = 3;
  function normLoadouts(v) {
    var src = Array.isArray(v) ? v : [];
    var out = [];
    for (var i = 0; i < LOADOUT_SLOTS; i++) {
      var s = src[i];
      var k = s && typeof s === 'object' ? (s.kit || null) : null;
      out.push(k && typeof k === 'object' ? { kit: norm(k) } : null);
    }
    return out;
  }
  // The state transition, kept OUT of the builder's closure so a harness can run it without a
  // DOM. Returns a new array and CLONES the kit: storing the live kit object by reference would
  // mean every subsequent edit to the avatar silently rewrote the look already saved.
  function loadoutSet(slots, i, kit) {
    var out = normLoadouts(slots);
    if (i < 0 || i >= LOADOUT_SLOTS) return out;
    out[i] = kit ? { kit: norm(assign({}, kit)) } : null;
    return out;
  }
  function presetFor(seed) {
    seed = String(seed || ''); var h = 0;
    for (var i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return assign({}, PRESETS[h % PRESETS.length].kit);
  }
  function assign(t, s) { for (var k in s) if (Object.prototype.hasOwnProperty.call(s, k)) t[k] = s[k]; return t; }

  // ---- drawing helpers ----------------------------------------------------
  function ink(d, fill, extra) {
    return '<path d="' + d + '" fill="' + (fill || 'none') + '" stroke="' + INK + '" stroke-width="' + LW +
      '" stroke-linejoin="round" stroke-linecap="round"' + (extra || '') + '/>';
  }
  function flat(d, fill) { return '<path d="' + d + '" fill="' + fill + '"/>'; }
  function n(v) { return Math.round(v * 10) / 10; }

  /* ---- the rig ------------------------------------------------------------
     joints() resolves a build + stance into concrete points. Everything that
     defines the outline is generated from these, so a stance change moves the
     shorts, the sleeves and the shoes with the limbs instead of leaving them
     behind on the old pose.
  -------------------------------------------------------------------------- */
  function joints(s, pose) {
    var b = s.b, lean = pose.lean || 0, hipTilt = pose.hipTilt || 0;
    var shL = [CX - b.sh + lean, SHOULDER], shR = [CX + b.sh + lean, SHOULDER];
    var hipL = [CX - b.hip * 0.46, s.HIP - hipTilt], hipR = [CX + b.hip * 0.46, s.HIP + hipTilt];
    var arms = [], legs = [];
    [shL, shR].forEach(function (sh, i) {
      var a = pose.arms[i];
      arms.push({ s: sh, e: [sh[0] + a.e[0], sh[1] + a.e[1]], h: [sh[0] + a.h[0], sh[1] + a.h[1]] });
    });
    [hipL, hipR].forEach(function (hp, i) {
      var l = pose.legs[i];
      legs.push({ s: hp, k: [hp[0] + l.k[0], hp[1] + l.k[1]], a: [hp[0] + l.a[0], hp[1] + l.a[1]], fd: l.fd });
    });
    return { shL: shL, shR: shR, arms: arms, legs: legs, lean: lean };
  }
  // Quadratic through the middle joint — one curve reads as a limb, two straight
  // segments read as a diagram.
  function limbD(a, mid, end) {
    return 'M' + n(a[0]) + ' ' + n(a[1]) + 'Q' + n(mid[0]) + ' ' + n(mid[1]) + ' ' + n(end[0]) + ' ' + n(end[1]);
  }
  function lerp(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }
  // A point ON the quadratic a limb is actually drawn from. lerp() along the
  // chord is close but not the same curve, and anything pinned to a limb (tape,
  // a band) drifts off it exactly where the bend is sharpest.
  function qpt(a, mid, c, t) {
    var u = 1 - t;
    return [u * u * a[0] + 2 * u * t * mid[0] + t * t * c[0],
            u * u * a[1] + 2 * u * t * mid[1] + t * t * c[1]];
  }

  /* A mitt with a thumb, rather than the plain ellipse v3 shipped with. At
     builder and profile sizes a bare oval on the end of an arm reads as a
     doll's peg, and it is the one part of the figure a member looks at while
     equipping a prop that sits right next to it.

     Drawn in a unit space around the wrist joint and scaled by r, with `side`
     flipping x so the thumb is always on the INNER edge — thumbs pointing away
     from the body is the single tell that makes a drawn hand look wrong.
     Deliberately ONE closed path, not a palm plus a thumb circle: two shapes
     would each carry the ink outline and leave a seam across the knuckle. */
  function handPath(h, r, side) {
    var o = side;                                  // +1 outer is right, -1 outer is left
    function px(v) { return n(h[0] + o * v * r); }
    function py(v) { return n(h[1] + v * r); }
    return 'M' + px(0) + ' ' + py(-1.05) +
      'C' + px(0.95) + ' ' + py(-1.05) + ' ' + px(0.98) + ' ' + py(1.18) + ' ' + px(0) + ' ' + py(1.18) +
      'C' + px(-0.92) + ' ' + py(1.18) + ' ' + px(-0.95) + ' ' + py(0.2) + ' ' + px(-0.74) + ' ' + py(-0.12) +
      'C' + px(-1.34) + ' ' + py(-0.46) + ' ' + px(-0.86) + ' ' + py(-1.06) + ' ' + px(-0.3) + ' ' + py(-0.74) +
      'C' + px(-0.2) + ' ' + py(-0.95) + ' ' + px(-0.1) + ' ' + py(-1.05) + ' ' + px(0) + ' ' + py(-1.05) + 'Z';
  }

  function torsoPath(s, J, crop) {
    var b = s.b, lean = J.lean;
    var wl = CX - b.waist + lean, wr = CX + b.waist + lean, waist = crop || s.WAIST;
    return 'M' + n(J.shL[0]) + ' ' + SHOULDER +
      'C' + n(J.shL[0] - 6) + ' ' + n(s.CHEST) + ' ' + n(wl - 8) + ' ' + n(waist - 52) + ' ' + n(wl) + ' ' + n(waist) +
      'L' + n(wr) + ' ' + n(waist) +
      'C' + n(wr + 8) + ' ' + n(waist - 52) + ' ' + n(J.shR[0] + 6) + ' ' + n(s.CHEST) + ' ' + n(J.shR[0]) + ' ' + SHOULDER +
      'C' + n(J.shR[0] - 22) + ' ' + n(SHOULDER - 28) + ' ' + n(CX + 46 + lean) + ' ' + n(SHOULDER - 42) + ' ' + n(CX + lean) + ' ' + n(SHOULDER - 42) +
      'C' + n(CX - 46 + lean) + ' ' + n(SHOULDER - 42) + ' ' + n(J.shL[0] + 22) + ' ' + n(SHOULDER - 28) + ' ' + n(J.shL[0]) + ' ' + SHOULDER + 'Z';
  }
  function neckPath() {
    return 'M' + n(CX - 34) + ' ' + NECK_TOP + 'L' + n(CX - 30) + ' ' + n(SHOULDER - 6) +
           'L' + n(CX + 30) + ' ' + n(SHOULDER - 6) + 'L' + n(CX + 34) + ' ' + NECK_TOP + 'Z';
  }

  /* Silhouette-defining primitives, in draw order. Decorative fills are NOT
     here on purpose — see the file header. */
  function bodyPrims(kit, s, pose, J, col) {
    var b = s.b, out = [];
    var sleeve = { long: 1, hoodie: 1, jacket: 1, tee: 0.42, crop: 0, singlet: 0 }[kit.top];
    if (sleeve == null) sleeve = 0.42;
    var hemT = { shorts: 0.44, skort: 0.30, tights: 0.98, joggers: 0.94 }[kit.bottom] || 0.44;
    var bare = (kit.top === 'singlet' || kit.top === 'crop');
    var cropTop = kit.top === 'crop';

    // ground shadow — anchors the figure, which otherwise floats
    var spread = Math.abs(J.legs[1].a[0] - J.legs[0].a[0]) / 2 + b.leg;
    out.push({ d: 'M' + CX + ' ' + n(s.GROUND + 16) + 'm-' + n(spread + 34) + ' 0a' + n(spread + 34) + ' 20 0 1 0 ' + n((spread + 34) * 2) + ' 0a' + n(spread + 34) + ' 20 0 1 0 -' + n((spread + 34) * 2) + ' 0',
               fill: 'rgba(0,0,0,.34)', w: 0, ink: false, noRim: true, noSil: true });

    // legs -> shoes -> bottoms, all following the SAME joints
    J.legs.forEach(function (lg) { out.push({ d: limbD(lg.s, lg.k, lg.a), fill: 'none', w: b.leg, col: col.skin, ink: true }); });
    /* The shoe has to CAP the leg. The leg above is a stroke of width b.leg with
       a ROUND cap centred on the ankle joint, so its skin reaches b.leg/2 below
       and to each side of the ankle (and the ink outline 7.5 past that). The old
       flat slab started at the ankle and sloped its heel straight up, so on the
       wider/heavier builds the round ankle cap poked out as a dark skin wedge
       behind and below the heel — the "shoes not covering" report. This is a
       side-profile shoe whose heel counter wraps DOWN to the sole behind the
       ankle and whose sole sits below the cap, so the leg terminal is always
       enclosed on every build and stance. Drawn from the ankle joint so it
       still tracks the foot into whatever pose the stance puts it in. */
    J.legs.forEach(function (lg) {
      var x = lg.a[0], y = lg.a[1], d = lg.fd;
      var R = b.leg / 2;              // leg (and its round cap) half-width
      var hw = R + 12;                // ankle collar half-width, wider than the cap
      var top = y - 22;               // collar top — leg enters from above here
      var sole = y + R + 15;          // ground line, clear below the cap + its ink
      var heelX = x - d * hw;         // heel counter sits BEHIND the ankle
      var toeX = x + d * (hw + 46);   // toe reaches forward in the foot direction
      out.push({ d:
        'M' + n(heelX) + ' ' + n(top + 6) +
        'Q' + n(heelX) + ' ' + n(sole) + ' ' + n(heelX + d * 24) + ' ' + n(sole) +          // heel counter down to sole
        'L' + n(toeX - d * 16) + ' ' + n(sole) +                                            // sole run to the toe
        'Q' + n(toeX) + ' ' + n(sole) + ' ' + n(toeX) + ' ' + n(sole - 20) +                // toe tip rounds up
        'Q' + n(toeX) + ' ' + n(sole - 34) + ' ' + n(x + d * hw * 0.5) + ' ' + n(top + 10) + // over the toe box to the instep
        'L' + n(x - d * hw * 0.2) + ' ' + n(top) +                                          // across the collar opening
        'Q' + n(heelX) + ' ' + n(top) + ' ' + n(heelX) + ' ' + n(top + 6) + 'Z',            // back up to the heel
        fill: col.shoe, w: 0, ink: true });
    });
    J.legs.forEach(function (lg) {
      var k = lerp(lg.s, lg.k, hemT), a = lerp(lg.s, lg.a, hemT);
      out.push({ d: 'M' + n(lg.s[0]) + ' ' + n(lg.s[1] - 40) + 'Q' + n(k[0]) + ' ' + n(k[1]) + ' ' + n(a[0]) + ' ' + n(a[1]),
                 fill: 'none', w: b.leg + 14, col: col.bottom, ink: true });
    });
    // hip block — makes the two legs read as one garment rather than two tubes
    out.push({ d: 'M' + n(J.legs[0].s[0] - b.hip * 0.42) + ' ' + n(s.HIP - 62) + 'H' + n(J.legs[1].s[0] + b.hip * 0.42) +
                  'L' + n(J.legs[1].s[0] + 6) + ' ' + n(s.HIP + 30) + 'L' + CX + ' ' + n(s.HIP - 4) +
                  'L' + n(J.legs[0].s[0] - 6) + ' ' + n(s.HIP + 30) + 'Z', fill: col.bottom, w: 0, ink: true, noRim: true });

    out.push({ d: neckPath(), fill: col.skin, w: 0, ink: true });

    // Arms go BEHIND the torso when they hang at the side and IN FRONT when the
    // stance folds them across the body — otherwise crossed arms disappear.
    var front = !!STANCE_HANDS_BUSY[kit.stance];
    function pushArms() {
      J.arms.forEach(function (ar) {
        out.push({ d: limbD(ar.s, ar.e, ar.h), fill: 'none', w: b.arm, col: col.skin, ink: true });
        if (sleeve > 0) {
          // Only a sleeve that stops short of the wrist needs a hem; a full
          // sleeve ends at the hand and wants the round cap so it tucks under it.
          out.push({ d: limbD(ar.s, lerp(ar.s, ar.e, sleeve), lerp(ar.s, ar.h, sleeve)),
                     fill: 'none', w: b.arm + 6, col: col.top, ink: true,
                     cap: sleeve < 1 ? 'butt' : 'round' });
        }
      });
    }
    if (!front) pushArms();

    // A crop top needs the bare torso UNDER it or the midriff renders as an
    // ink-dark hole instead of skin.
    if (bare) out.push({ d: torsoPath(s, J), fill: col.skin, w: 0, ink: true });
    if (cropTop) out.push({ d: torsoPath(s, J, s.WAIST - CROP_DROP), fill: col.top, w: 0, ink: true });
    else out.push({ d: torsoPath(s, J), fill: col.top, w: 0, ink: true });

    /* Everything that decorates the TORSO belongs here — after the shirt,
       before any arm folded across it. Emitting it at the end instead painted
       the rugby hoops, the collar and the race bib straight over the forearms
       in the hips and crossed stances. A sentinel in the ordered list beats
       returning two arrays: the order stays readable in one place, and the rim
       and silhouette passes just skip it. */
    out.push({ deco: true, noRim: true, noSil: true });
    if (front) pushArms();

    // hands — the terminals that make props possible at all
    J.arms.forEach(function (ar, i) {
      out.push({ d: handPath(ar.h, b.arm * 0.62, i === 0 ? -1 : 1), fill: col.skin, w: 0, ink: true });
    });

    // prop — held in a hand, or standing on the ground beside the feet
    var rp = resolveProp(kit);
    if (rp) {
      var prop = rp.p, away = prop.hand === 1 ? 1 : -1, off = prop.off || [0, 0], tx, ty, draw;
      if (rp.mode === 'ground') {
        // Beside the foot on that side, clear of the stance's widest step, and
        // translated so the drawing's own lowest point lands ON the ground line
        // rather than at whatever y its origin happens to be.
        var foot = J.legs[prop.hand].a;
        tx = foot[0] + away * (b.leg * 0.5 + 96);
        ty = s.GROUND - PROP_SCALE * (prop.gy || 0);
        draw = prop.gd ? prop.gd() : prop.d();
      } else {
        var hand = J.arms[prop.hand].h;
        // A raised item sits INBOARD of the hand, not outboard. At the side
        // there is open frame to push a ball into; over the head the arm is
        // already near the edge, so pushing further out only means the clamp
        // below drags the item back and detaches it from the fist. Inboard also
        // happens to be how a person actually holds something aloft.
        var overhead = stanceOf(kit.stance).grip === 'overhead';
        tx = overhead ? hand[0] - away * (off[0] * 0.4 + 8)
                      : hand[0] + away * (off[0] + 26);
        ty = hand[1] + off[1] * (overhead ? 0.4 : 1);
        draw = prop.d();
      }
      /* Clamp the item into the frame. A held ball on a Strong build already
         reached x 821 against a right edge of 790, and it was invisible in
         review because the first look was at the Athletic build, where it fits.
         Declaring each prop's half-width and clamping is the only version of
         this that cannot rot: widening the box alone fixes today's worst case
         and says nothing about the next prop somebody draws. `_verify/avatar/`
         asserts the whole matrix of stance x build x prop stays inside. */
      // A prop whose SET-DOWN drawing is a different shape needs its own
      // half-width: the rope coil is wider than the rope hanging from a hand,
      // and clamping the coil by the hanging figure let it out of the frame.
      // Reserve the OUTLINE too, not just the fill. Every prop is drawn with the
      // ink stroke (LW) and, behind it, the die-cut rim (LW + RIM*2 wide), so its
      // true extent is ~LW/2 + RIM beyond the fill edge. Clamping on the fill
      // half-width alone let a held basketball / netball's rim slice flat against
      // the right frame edge on most builds — a real clip, not anti-aliasing.
      var halfW = ((rp.mode === 'ground' && prop.ghw) || prop.hw || 0) * PROP_SCALE + LW + RIM, pad = 10;
      tx = Math.max(BOX_FULL.x + pad + halfW, Math.min(BOX_FULL.x + BOX_FULL.w - pad - halfW, tx));
      draw.forEach(function (p) {
        out.push({ d: p.d, fill: p.fill, w: p.w, col: p.col, ink: p.ink,
                   tx: tx, ty: ty, sc: PROP_SCALE });
      });
    }
    return out;
  }

  /* 'auto' means "whatever my sport carries". Where the item GOES is then a
     function of the stance's grip:

       side     (ready, stride)   -> held at the athlete's side
       overhead (victory)         -> raised, WHATEVER it is
       none     (hips, crossed)   -> set down at the athlete's feet

     Two earlier versions of this were wrong, in opposite directions. The first
     deleted the prop whenever both hands were busy, which loses the member's
     item and costs the silhouette the one thing breaking the outline. The
     second set heavy items down in the victory stance too, on the argument that
     a kettlebell over the head reads as a lift rather than a celebration —
     owner overruled it (2026-08-07), and they are right: a member who finishes
     a Hyrox and puts their kettlebell on the floor is not celebrating either.
     Everything goes up now, and the `raise` flag that encoded the old rule is
     gone rather than left dangling. */
  function resolveProp(kit) {
    var id = kit.prop === 'auto' ? (sportOf(kit.sport).prop || 'none') : kit.prop;
    var p = propOf(id);
    if (!p || p.empty) return null;
    return { p: p, mode: stanceOf(kit.stance).grip === 'none' ? 'ground' : 'hand' };
  }

  // ---- the three passes ---------------------------------------------------
  function emit(p, mode, rimCol) {
    if (p.deco) return '';                 // layer marker, not a shape
    var t = p.tx != null
      ? ' transform="translate(' + n(p.tx) + ' ' + n(p.ty) + ')' + (p.sc ? ' scale(' + p.sc + ')' : '') + '"' : '';
    // A short sleeve is a stroke that stops partway down the arm. With a round
    // cap that stop becomes a bulb, and a cap sleeve rendered as a lime water
    // wing on the shoulder. `cap:'butt'` gives it a hem.
    var join = ' stroke-linejoin="round" stroke-linecap="' + (p.cap || 'round') + '"';
    // A scaled prop would otherwise get a scaled line weight, and the whole
    // figure is meant to read as one hand-inked drawing at ONE weight.
    var k = p.sc || 1;
    if (mode === 'rim') {
      if (p.noRim) return '';
      return '<path d="' + p.d + '" fill="' + (p.fill === 'none' ? 'none' : rimCol) + '" stroke="' + rimCol +
        '" stroke-width="' + n(((p.w || 0) + LW + RIM * 2) / k) + '"' + join + t + '/>';
    }
    if (mode === 'sil') {
      if (p.noSil) return '';
      return '<path d="' + p.d + '" fill="' + (p.fill === 'none' ? 'none' : '#000') + '" stroke="#000" stroke-width="' +
        n(((p.w || 0) + LW) / k) + '"' + join + t + '/>';
    }
    var g = '';
    if (p.ink) g += '<path d="' + p.d + '" fill="' + (p.fill === 'none' ? 'none' : INK) + '" stroke="' + INK +
      '" stroke-width="' + n(((p.w || 0) + LW) / k) + '"' + join + t + '/>';
    g += '<path d="' + p.d + '" fill="' + p.fill + '"' +
      (p.w ? ' stroke="' + (p.col || p.fill) + '" stroke-width="' + p.w + '"' : ' stroke="none"') + join + t + '/>';
    return g;
  }

  // ---- decorative fills (drawn after the body, excluded from rim/silhouette)
  // Collar cut-outs — the fastest read of "which sport is this" up close, even
  // though the measurement says they do nothing at all at thumbnail size.
  function collarSVG(kind, b, J) {
    var y = SHOULDER - 34, cx = CX + J.lean;
    switch (kind) {
      case 'vee':  return ink('M' + n(cx - 46) + ' ' + n(y) + 'L' + n(cx) + ' ' + n(y + 76) + 'L' + n(cx + 46) + ' ' + n(y), 'none');
      case 'zip':  return ink('M' + n(cx) + ' ' + n(y) + 'L' + n(cx) + ' ' + n(y + 220), 'none');
      // A polo is two small lapels either side of a short placket. Drawing it as
      // one continuous zigzag turned it into a giant "W" across the chest.
      case 'polo': return ink('M' + n(cx - 54) + ' ' + n(y - 2) + 'L' + n(cx - 14) + ' ' + n(y + 30), 'none') +
                          ink('M' + n(cx + 54) + ' ' + n(y - 2) + 'L' + n(cx + 14) + ' ' + n(y + 30), 'none') +
                          ink('M' + n(cx - 14) + ' ' + n(y + 30) + 'L' + n(cx - 12) + ' ' + n(y + 92), 'none') +
                          ink('M' + n(cx + 14) + ' ' + n(y + 30) + 'L' + n(cx + 12) + ' ' + n(y + 92), 'none');
      case 'tank': return ink('M' + n(cx - b.sh + 40) + ' ' + n(y - 10) + 'C' + n(cx - 40) + ' ' + n(y + 90) + ' ' + n(cx + 40) + ' ' + n(y + 90) + ' ' + n(cx + b.sh - 40) + ' ' + n(y - 10), 'none');
      default:     return ink('M' + n(cx - 44) + ' ' + n(y - 2) + 'C' + n(cx - 26) + ' ' + n(y + 30) + ' ' + n(cx + 26) + ' ' + n(y + 30) + ' ' + n(cx + 44) + ' ' + n(y - 2), 'none');
    }
  }
  // Fabric patterns, clipped to the torso so nothing bleeds off the shirt.
  function patternSVG(kind, s, J, accent, cid) {
    var b = s.b, cx = CX + J.lean, g = '<g clip-path="url(#' + cid + ')">';
    switch (kind) {
      case 'hoops':
        for (var y = SHOULDER + 20; y < s.WAIST; y += 62) g += flat('M' + n(cx - b.sh - 20) + ' ' + n(y) + 'h' + n(b.sh * 2 + 40) + 'v32h-' + n(b.sh * 2 + 40) + 'Z', accent);
        break;
      case 'vstripe':
        g += flat('M' + n(cx - 34) + ' ' + SHOULDER + 'h68v' + n(s.WAIST - SHOULDER) + 'h-68Z', accent);
        break;
      case 'diagonal':
        g += flat('M' + n(cx - b.sh - 30) + ' ' + n(s.WAIST) + 'L' + n(cx + 20) + ' ' + n(SHOULDER - 40) + 'h74L' + n(cx - b.sh + 44) + ' ' + n(s.WAIST) + 'Z', accent);
        break;
      case 'panel':
        g += flat('M' + n(cx - b.sh - 20) + ' ' + SHOULDER + 'h56v' + n(s.WAIST - SHOULDER) + 'h-56Z', accent);
        g += flat('M' + n(cx + b.sh - 36) + ' ' + SHOULDER + 'h56v' + n(s.WAIST - SHOULDER) + 'h-56Z', accent);
        break;
      case 'shoulder':
        g += flat('M' + n(cx - b.sh - 20) + ' ' + SHOULDER + 'h' + n(b.sh * 2 + 40) + 'v46h-' + n(b.sh * 2 + 40) + 'Z', accent);
        break;
      case 'band':
        g += flat('M' + n(cx - b.sh - 20) + ' ' + n(s.WAIST - 74) + 'h' + n(b.sh * 2 + 40) + 'v44h-' + n(b.sh * 2 + 40) + 'Z', accent);
        break;
      case 'bibpanel':
        g += flat('M' + n(cx - 84) + ' ' + n(SHOULDER + 24) + 'h168v150h-168Z', '#FFFFFF');
        g += ink('M' + n(cx - 84) + ' ' + n(SHOULDER + 24) + 'h168v150h-168Z', 'none');
        break;
    }
    return g + '</g>';
  }
  // Kit furniture — the props that make a sport unmistakable up close.
  function kitExtras(sp, s, J) {
    var b = s.b, cx = CX + J.lean, g = '';
    if (sp.bib) {
      g += ink('M' + n(cx - 56) + ' ' + n(s.CHEST + 16) + 'h112v78h-112Z', '#FFFFFF');
      g += flat('M' + n(cx - 34) + ' ' + n(s.CHEST + 38) + 'h18v34h-18Z', INK);
      g += flat('M' + n(cx - 9) + ' ' + n(s.CHEST + 38) + 'h18v34h-18Z', INK);
      g += flat('M' + n(cx + 16) + ' ' + n(s.CHEST + 38) + 'h18v34h-18Z', INK);
    }
    if (sp.number) {
      g += flat('M' + n(cx - 40) + ' ' + n(s.CHEST + 10) + 'h24v96h-24Z', '#FFFFFF');
      g += flat('M' + n(cx + 14) + ' ' + n(s.CHEST + 10) + 'h26v26h-26Z', '#FFFFFF');
      g += flat('M' + n(cx + 14) + ' ' + n(s.CHEST + 80) + 'h26v26h-26Z', '#FFFFFF');
      g += flat('M' + n(cx + 14) + ' ' + n(s.CHEST + 45) + 'h26v26h-26Z', '#FFFFFF');
    }
    if (sp.belt) g += ink('M' + n(cx - b.waist - 8) + ' ' + n(s.WAIST - 66) + 'h' + n(b.waist * 2 + 16) + 'v72h-' + n(b.waist * 2 + 16) + 'Z', '#5A3A1E');
    if (sp.harness) {
      g += ink('M' + n(cx - b.waist - 6) + ' ' + n(s.WAIST - 30) + 'h' + n(b.waist * 2 + 12) + 'v44h-' + n(b.waist * 2 + 12) + 'Z', sp.accent);
      g += ink('M' + n(cx - 70) + ' ' + n(s.WAIST + 14) + 'C' + n(cx - 90) + ' ' + n(s.HIP + 40) + ' ' + n(cx - 30) + ' ' + n(s.HIP + 50) + ' ' + n(cx - 14) + ' ' + n(s.HIP + 20), 'none');
      g += ink('M' + n(cx + 70) + ' ' + n(s.WAIST + 14) + 'C' + n(cx + 90) + ' ' + n(s.HIP + 40) + ' ' + n(cx + 30) + ' ' + n(s.HIP + 50) + ' ' + n(cx + 14) + ' ' + n(s.HIP + 20), 'none');
    }
    if (sp.goggles) {
      g += ink('M' + n(cx - 58) + ' ' + n(SHOULDER + 6) + 'C' + n(cx - 20) + ' ' + n(SHOULDER + 54) + ' ' + n(cx + 20) + ' ' + n(SHOULDER + 54) + ' ' + n(cx + 58) + ' ' + n(SHOULDER + 6), 'none');
      g += ink('M' + n(cx - 34) + ' ' + n(SHOULDER + 34) + 'h30v26h-30Z', sp.accent);
      g += ink('M' + n(cx + 6) + ' ' + n(SHOULDER + 34) + 'h30v26h-30Z', sp.accent);
    }
    return g;
  }
  // Accessories now hang off the ACTUAL joints, so mitts and a wristband follow
  // the hand into whatever stance the member picked instead of sitting at the
  // fixed y=980 v2 assumed every hand would always be at.
  /* Split in two (2026-08-07) because layer order and pose-tracking are
     different problems and the accessories fall on both sides of them:

       TORSO  — vest, belt, medal. Live on the chest and waist, so they must be
                drawn with the other torso decoration, BEFORE the arms in a
                folded-arm stance. Painted after, a medal sat on top of the
                forearms.
       LIMB   — k-tape, mitts, wristband. Live on the arms and legs, so they
                must follow the JOINTS and be drawn last, over the limbs.

     The k-tape was the visible bug: its arm strips were pinned to CX +/- sh at
     chest height, which is where the shoulders are, not where the arms went.
     In every stance except Ready they floated in mid-air beside the body. */
  function accessoryTorso(acc, s, J) {
    var b = s.b, cx = CX + J.lean;
    switch (acc) {
      case 'vest':
        return ink('M' + n(cx - 62) + ' ' + n(SHOULDER - 20) + 'L' + n(cx - 48) + ' ' + n(s.WAIST - 40), 'none') +
               ink('M' + n(cx + 62) + ' ' + n(SHOULDER - 20) + 'L' + n(cx + 48) + ' ' + n(s.WAIST - 40), 'none') +
               ink('M' + n(cx - 92) + ' ' + n(s.CHEST + 20) + 'h50v70h-50Z', '#B7FF00') +
               ink('M' + n(cx + 42) + ' ' + n(s.CHEST + 20) + 'h50v70h-50Z', '#B7FF00');
      case 'belt':
        return ink('M' + n(cx - b.waist - 8) + ' ' + n(s.WAIST - 62) + 'h' + n(b.waist * 2 + 16) + 'v70h-' + n(b.waist * 2 + 16) + 'Z', '#5A3A1E') +
               ink('M' + n(cx - 22) + ' ' + n(s.WAIST - 46) + 'h44v40h-44Z', '#CAA400');
      case 'medal':
        return ink('M' + n(cx - 34) + ' ' + n(SHOULDER - 20) + 'L' + n(cx - 8) + ' ' + n(s.CHEST + 70), 'none') +
               ink('M' + n(cx + 34) + ' ' + n(SHOULDER - 20) + 'L' + n(cx + 8) + ' ' + n(s.CHEST + 70), 'none') +
               ink('M' + n(cx) + ' ' + n(s.CHEST + 74) + 'm-40 0a40 40 0 1 0 80 0a40 40 0 1 0 -80 0', '#FFD84D');
    }
    return '';
  }
  function accessoryLimb(acc, s, J) {
    var b = s.b, hL = J.arms[0].h, hR = J.arms[1].h;
    function atHand(h, w, ht2, fill, dy) {
      return ink('M' + n(h[0] - w / 2) + ' ' + n(h[1] + (dy || 0) - ht2 / 2) + 'h' + n(w) + 'v' + n(ht2) + 'h-' + n(w) + 'Z', fill);
    }
    // A strip of tape laid ALONG a limb, between two points on the same
    // quadratic the limb is drawn from — so it curves with the arm rather than
    // being a rectangle stapled near it.
    function tape(a, mid, c, t0, t1, col, w) {
      var p0 = qpt(a, mid, c, t0), p1 = qpt(a, mid, c, t1);
      return '<path d="M' + n(p0[0]) + ' ' + n(p0[1]) + 'L' + n(p1[0]) + ' ' + n(p1[1]) +
        '" fill="none" stroke="' + col + '" stroke-width="' + n(w) + '" stroke-linecap="round"/>';
    }
    switch (acc) {
      case 'ktape':
        return tape(J.arms[0].s, J.arms[0].e, J.arms[0].h, 0.46, 0.72, '#FF4D6D', b.arm * 0.62) +
               tape(J.arms[1].s, J.arms[1].e, J.arms[1].h, 0.46, 0.72, '#15C7A8', b.arm * 0.62) +
               tape(J.legs[0].s, J.legs[0].k, J.legs[0].a, 0.40, 0.62, '#2533F5', b.leg * 0.60) +
               tape(J.legs[1].s, J.legs[1].k, J.legs[1].a, 0.40, 0.62, '#FFD84D', b.leg * 0.60);
      case 'gloves':
        return atHand(hL, 52, 56, INK) + atHand(hR, 52, 56, INK);
      case 'wristband':
        return atHand(hL, 48, 34, '#B7FF00', -52) + atHand(hR, 48, 34, '#B7FF00', -52);
    }
    return '';
  }

  /* Per-garment detailing. The wardrobe separates tops mainly by SLEEVE length
     and bottoms by HEM length, which left hoodie / long / jacket reading as one
     garment and joggers as tights. These are the marks that give each its own
     read — a hood and pocket, a jacket zip, jogger cuffs, a skort flare — drawn
     at the torso-decoration layer (over the garment, under a folded arm). Same
     colour as the garment so they are texture, not a second colour to manage. */
  function garmentExtras(kit, s, J, col) {
    var b = s.b, cx = CX + J.lean, g = '';
    if (kit.top === 'hoodie') {
      // Hood worn down: a rolled cowl bunched at the neck, plus drawstrings and
      // a kangaroo pocket. The head draws last and over the top of the cowl, so
      // what shows is the roll sitting around the neck and shoulders.
      var hy = SHOULDER - 34;
      g += ink('M' + n(cx - 66) + ' ' + n(hy) +
               'C' + n(cx - 58) + ' ' + n(hy + 70) + ' ' + n(cx + 58) + ' ' + n(hy + 70) + ' ' + n(cx + 66) + ' ' + n(hy) +
               'C' + n(cx + 38) + ' ' + n(hy + 34) + ' ' + n(cx - 38) + ' ' + n(hy + 34) + ' ' + n(cx - 66) + ' ' + n(hy) + 'Z', col.top);
      g += ink('M' + n(cx - 12) + ' ' + n(hy + 44) + 'v62', 'none');
      g += ink('M' + n(cx + 12) + ' ' + n(hy + 44) + 'v56', 'none');
      g += flat('M' + n(cx - 16) + ' ' + n(hy + 104) + 'h9v16h-9Z', INK);
      g += flat('M' + n(cx + 8) + ' ' + n(hy + 98) + 'h9v16h-9Z', INK);
      var py = s.WAIST - 52;
      g += ink('M' + n(cx - 78) + ' ' + n(py) + 'h156v58h-156Z', 'none');
      g += ink('M' + n(cx - 52) + ' ' + n(py + 8) + 'l-14 42', 'none');
      g += ink('M' + n(cx + 52) + ' ' + n(py + 8) + 'l14 42', 'none');
    } else if (kit.top === 'jacket') {
      // Track jacket: centre zip, a stand collar and a pull tab.
      g += ink('M' + n(cx) + ' ' + n(SHOULDER - 34) + 'V' + n(s.WAIST - 6), 'none');
      g += ink('M' + n(cx - 46) + ' ' + n(SHOULDER - 40) + 'l32 30', 'none');
      g += ink('M' + n(cx + 46) + ' ' + n(SHOULDER - 40) + 'l-32 30', 'none');
      g += flat('M' + n(cx - 5) + ' ' + n(SHOULDER - 6) + 'h10v20h-10Z', INK);
    }
    if (kit.bottom === 'joggers') {
      // Elastic ankle cuffs — the tell between joggers and tights.
      J.legs.forEach(function (lg) {
        var c = qpt(lg.s, lg.k, lg.a, 0.88), hw = b.leg / 2 + 9;
        g += ink('M' + n(c[0] - hw) + ' ' + n(c[1] - 14) + 'h' + n(hw * 2) + 'v28h-' + n(hw * 2) + 'Z', col.bottom);
        g += ink('M' + n(c[0] - hw) + ' ' + n(c[1]) + 'h' + n(hw * 2), 'none');
      });
    } else if (kit.bottom === 'skort') {
      // An A-line skirt draped over the shorts underneath.
      var ky = s.HIP - 4, kw = b.hip + 26, kd = kw + 44;
      g += ink('M' + n(cx - kw) + ' ' + n(ky) +
               'L' + n(cx - kd) + ' ' + n(ky + 96) +
               'Q' + n(cx) + ' ' + n(ky + 122) + ' ' + n(cx + kd) + ' ' + n(ky + 96) +
               'L' + n(cx + kw) + ' ' + n(ky) + 'Z', col.bottom);
    }
    return g;
  }

  // ---- head ---------------------------------------------------------------
  function peepsHead(kit, sk, hc, cloth, tilt, rim, flatten) {
    var P = window.HubPeeps;
    if (!P) return fallbackHead(sk, hc);
    function sub(s) { return s.split('__SKIN__').join(sk).split('__HAIR__').join(hc).split('__CLOTH__').join(cloth); }
    /* HAIR COLOUR HAS TO BE PAINTED ON, not substituted (fixed 2026-08-07).
       Only ONE of the 34 Open Peeps hairstyles (mohawk) carries a __HAIR__
       token — every other one, and every beard, is a single hard-coded
       fill="#000" mass. So the hair-colour row had never done anything on 33 of
       34 styles, in v2 either; it looked wired up because the one style anybody
       tested it on happened to be the exception.
       Tint ONLY hair and beard. The face's #000 is its LINEWORK — eyes, brows,
       mouth, jaw — and eyewear's #000 is the frame; tinting those turns a face
       lime. */
    function tint(s, col) { return s.split('fill="#000"').join('fill="' + col + '"'); }
    // Some Open Peeps paths carry literal fills (eye whites, teeth). Left alone
    // they punch white holes through the black mask and the silhouette gate
    // would be measuring the holes instead of the shape.
    function force(s, col) { return s.replace(/fill="[^"]*"/g, 'fill="' + col + '"'); }

    var A = P.anchor, g = '';
    var hr = P.parts.hair[kit.hair], fc = P.parts.face[kit.face];
    var hrM = hr ? '<g transform="' + A.hair + '">' + tint(sub(hr.s), hc) + '</g>' : '';
    var fcM = fc ? '<g transform="' + A.face + '">' + sub(fc.s) + '</g>' : '';
    g += hrM + fcM;
    var beard = kit.beard && kit.beard !== 'none' ? P.parts.beard[kit.beard] : null;
    if (beard) g += '<g transform="' + A.beard + '">' + tint(sub(beard.s), hc) + '</g>';
    var gl = kit.glasses && kit.glasses !== 'none' ? P.parts.glasses[kit.glasses] : null;
    if (gl) g += '<g transform="' + A.glasses + '">' + sub(gl.s) + '</g>';
    if (flatten) g = force(g, '#000');

    /* The rim is a BACKING copy of the hair and face only — every fill flooded
       to the rim colour and re-stroked fat — drawn behind the real head. The
       obvious one-liner, paint-order="stroke" on the live group, rims every
       interior path too: the eyes, brows and mouth all come back outlined and
       the face reads as a sketch. Beard and eyewear are left out of the backing
       because they sit inside the head outline and add only weight. */
    var back = (rim && !flatten)
      ? '<g fill="' + rim + '" stroke="' + rim + '" stroke-width="' + (RIM * 2) + '" stroke-linejoin="round">' + force(hrM + fcM, rim) + '</g>'
      : '';
    return '<g transform="rotate(' + (tilt || 0) + ' ' + CX + ' 620)">' + back + g + '</g>';
  }
  // Used only when hub-peeps.js is missing — plain, but never a blank square.
  function fallbackHead(sk, hc) {
    return ink('M' + n(CX) + ' 300m-150 0a150 150 0 1 0 300 0a150 150 0 1 0 -300 0', sk) +
      ink('M' + n(CX - 150) + ' 280C' + n(CX - 150) + ' 100 ' + n(CX + 150) + ' 100 ' + n(CX + 150) + ' 280C' + n(CX + 90) + ' 200 ' + n(CX - 90) + ' 200 ' + n(CX - 150) + ' 280Z', hc) +
      flat('M' + n(CX - 60) + ' 300m-16 0a16 16 0 1 0 32 0a16 16 0 1 0 -32 0', INK) +
      flat('M' + n(CX + 60) + ' 300m-16 0a16 16 0 1 0 32 0a16 16 0 1 0 -32 0', INK);
  }

  // ---- frames -------------------------------------------------------------
  var DEFAULT_BG = '#151A22';    // on-brand carbon, not the old pale grey disc
  // `box` is the active viewBox; the plate and the ring are drawn from it so
  // they frame whichever crop is in use instead of a hardcoded square.
  function frameSVG(fr, hexBg, architect, uid, box) {
    var base = (hexBg && HEXOK.test(hexBg)) ? hexBg : DEFAULT_BG;
    function plate(fill) {
      return '<rect x="' + box.x + '" y="' + box.y + '" width="' + box.w + '" height="' + box.h + '" fill="' + fill + '"/>';
    }
    /* THE ARCHITECT PLATE IS A BONUS, NOT A HIJACK (fixed 2026-08-07).
       This used to return early for architects, which meant every frame in the
       picker did nothing for them — they clicked Lime, Champion, Holo and got
       the same purple aurora every time. That is precisely the "frame not
       working" report, and it only ever reproduced for accounts carrying
       window.HUB_ARCHITECT, which is why it survived review.
       Architects keep the aurora PLATE (it is the thing that marks them), but
       the RING follows whatever frame they chose. Choosing 'ring' — the
       default, i.e. no deliberate choice — still yields the purple ring, so an
       architect who never touches the picker looks exactly as before. */
    var aur = architect
      ? { bg: plate('url(#aur' + uid + ')'),
          defs: '<radialGradient id="aur' + uid + '" cx="50%" cy="38%" r="72%"><stop offset="0" stop-color="#d8b4fe"/><stop offset=".5" stop-color="#7e22ce"/><stop offset="1" stop-color="#2e1065"/></radialGradient>' }
      : null;
    if (aur && (fr === 'ring' || !fr)) return { bg: aur.bg, ring: '#a855f7', defs: aur.defs };
    if (aur) {
      var chosen = frameSVG(fr, hexBg, false, uid, box);
      return { bg: aur.bg, ring: chosen.ring, defs: aur.defs + (chosen.defs || '') };
    }
    switch (fr) {
      case 'lime':   return { bg: plate(base), ring: '#B7FF00' };
      case 'grad':   return { bg: plate('url(#gr' + uid + ')'), ring: '#2533F5',
                              defs: '<radialGradient id="gr' + uid + '" cx="34%" cy="22%" r="86%"><stop offset="0" stop-color="#2533F5"/><stop offset="1" stop-color="' + base + '"/></radialGradient>' };
      case 'holo':   return { bg: plate('url(#ho' + uid + ')'), ring: '#ffffff',
                              defs: '<linearGradient id="ho' + uid + '" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#FF4D6D"/><stop offset=".35" stop-color="#FFD84D"/><stop offset=".6" stop-color="#15C7A8"/><stop offset="1" stop-color="#7A4DFF"/></linearGradient>' };
      case 'laurel': return { bg: plate(base), ring: '#FFD84D' };
      case 'none':   return { bg: '', ring: '' };
      default:       return { bg: plate(base), ring: '#2A2F36' };
    }
  }

  // ---- compose ------------------------------------------------------------
  // Head crop is the workhorse (chips, header avatars, leaderboard rows) and is
  // square; it must fit the TALLEST hair, not the average one (an afro reaches
  // x 46..706 and a top-knot starts at y -4, so a tight square decapitates them).
  // The full-body box is widened to fit the widest PROP, not the widest body,
  // and stays centred on CX so a member holding nothing is not pushed off-centre.
  var BOX_HEAD = { x: 96, y: -16, w: 570, h: 570 };
  /* The frame ring is drawn on this box, so the box IS the card. Widening it to
     1000 to give a raised dumbbell room left the ring reading as a loose empty
     card around a small figure — owner asked for it tightened (2026-08-07).
     Back to a portrait card that hugs the athlete. What pays for it: the
     Finish-line V comes in and goes UP instead of out, and a held prop clamps
     closer to the body. Centre stays on CX=380 (10 + 740/2). Width does not
     affect the silhouette gate — the mask is 48 TALL, so the figure's pixel
     size follows box HEIGHT only. */
  // 772, not 740. At 740 the mid-stride shoe clipped the left edge by 2-3 units
  // on whichever build happened to be widest, and chasing that per build is
  // whack-a-mole: the shoe's toe length and the stance offsets both scale, so
  // there is always a next build. A small symmetric margin costs 1.5% of card
  // width and ends the game. Centre stays on CX=380 (-6 + 772/2).
  var BOX_FULL = { x: -6, y: -40, w: 772, h: 1560 };

  var uidSeq = 0;
  function svg(kit, opts) {
    kit = norm(kit); opts = opts || {};
    var size = opts.size || 132;
    var uid = 'a' + (++uidSeq);
    var b = buildOf(kit.build), s = skel(b), pose = stanceOf(kit.stance).f(s), J = joints(s, pose);
    var col = {
      skin: colorOf('skin', kit.skin) || SKIN[3],
      hair: colorOf('hairColor', kit.hairColor) || HAIRC[0],
      top: colorOf('topColor', kit.topColor) || CLOTH[0],
      bottom: colorOf('bottomColor', kit.bottomColor) || CLOTH[1],
      shoe: colorOf('shoe', kit.shoe) || SHOEC[0]
    };
    var sp = sportOf(kit.sport), accent = sp.accent || '#B7FF00';
    var crop = opts.crop === 'head';
    var box = crop ? BOX_HEAD : BOX_FULL;
    var vb = box.x + ' ' + box.y + ' ' + box.w + ' ' + box.h;
    var w = crop ? size : Math.round(size * box.w / box.h);

    /* The three member-chosen borders. Each one only takes effect when the
       member actually picked something, so 'auto' everywhere reproduces the
       old render exactly — which is what every existing saved kit is.
       A chosen PLATE beats the architect aurora on purpose: leaving the aurora
       on top would recreate, for architects only, the same "my choice does
       nothing" bug that frameSVG's early return caused. An architect who picks
       nothing still gets the aurora. */
    var plate = pickColor('plateColor', kit.plateColor, opts.bg);
    var arch = !!opts.architect && kit.plateColor === 'auto';
    var fr = frameSVG(kit.frame, plate, arch, uid, box);
    if (fr.ring) fr.ring = pickColor('frameColor', kit.frameColor, fr.ring);

    // opts.rim stays the caller's override (the harness switches it off); the
    // member's choice sits under it, and the neutral default under that.
    var rimCol = opts.rim === false ? null
      : (opts.rim || pickColor('rimColor', kit.rimColor, RIM_COLOR));

    var body = '', defs = '';
    if (!crop) {
      var prims = bodyPrims(kit, s, pose, J, col);
      // The fabric pattern is clipped to the shirt. A crop top only covers to the
      // crop hem, so the clip has to stop there too — otherwise the pattern
      // (rugby hoops, a cycling stripe…) bleeds down onto the exposed midriff.
      var clip = kit.top === 'crop' ? torsoPath(s, J, s.WAIST - CROP_DROP) : torsoPath(s, J);
      defs = '<defs><clipPath id="clip' + uid + '"><path d="' + clip + '"/></clipPath>' + (fr.defs || '') + '</defs>';
      // Torso decoration is built once and injected at the sentinel, so it
      // lands under any arm the stance folds across the chest.
      var deco = (kit.sport !== 'none')
        ? patternSVG(sp.pattern, s, J, accent, 'clip' + uid) + collarSVG(sp.collar, b, J) + kitExtras(sp, s, J)
        : collarSVG('crew', b, J);
      deco += garmentExtras(kit, s, J, col);
      deco += accessoryTorso(kit.accessory, s, J);

      if (rimCol) prims.forEach(function (p) { body += emit(p, 'rim', rimCol); });
      prims.forEach(function (p) { body += p.deco ? deco : emit(p, 'normal', rimCol); });
      // limb-worn accessories go last, over the arms and legs they sit on
      body += accessoryLimb(kit.accessory, s, J);
    } else {
      defs = '<defs>' + (fr.defs || '') + '</defs>';
      if (rimCol) body += emit({ d: neckPath(), fill: col.skin, w: 0, ink: true }, 'rim', rimCol);
      body += emit({ d: neckPath(), fill: col.skin, w: 0, ink: true }, 'normal', rimCol);
    }

    var sw2 = crop ? 12 : 16;
    var ring = fr.ring ? '<rect x="' + (box.x + sw2 / 2) + '" y="' + (box.y + sw2 / 2) +
      '" width="' + (box.w - sw2) + '" height="' + (box.h - sw2) +
      '" fill="none" stroke="' + fr.ring + '" stroke-width="' + sw2 + '" rx="' + (crop ? 28 : 44) + '"/>' : '';

    return '<svg class="hub-av" viewBox="' + vb + '" width="' + w + '" height="' + size +
      '" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Custom athlete avatar" preserveAspectRatio="xMidYMid meet">' +
      defs + fr.bg + body + peepsHead(kit, col.skin, col.hair, col.top, pose.tilt, rimCol, false) + ring + '</svg>';
  }

  /* The gate. Renders the athlete as a solid black shape on white — the same
     primitive list the normal render uses, so it cannot drift from what a
     member actually sees. _verify/avatar/ rasterises this to a 48px mask and
     asserts every sport moves the silhouette by at least SIL_FLOOR%. */
  var SIL_FLOOR = 3;
  function silhouette(kit, size) {
    kit = norm(kit);
    var b = buildOf(kit.build), s = skel(b), pose = stanceOf(kit.stance).f(s), J = joints(s, pose);
    var col = { skin: '#000', top: '#000', bottom: '#000', shoe: '#000', hair: '#000' };
    var box = BOX_FULL, body = '';
    bodyPrims(kit, s, pose, J, col).forEach(function (p) { body += emit(p, 'sil', '#000'); });
    body += peepsHead(kit, '#000', '#000', '#000', pose.tilt, null, true);
    size = size || 300;
    return '<svg viewBox="' + box.x + ' ' + box.y + ' ' + box.w + ' ' + box.h +
      '" width="' + Math.round(size * box.w / box.h) + '" height="' + size +
      '" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">' +
      '<rect x="' + box.x + '" y="' + box.y + '" width="' + box.w + '" height="' + box.h + '" fill="#FFFFFF"/>' + body + '</svg>';
  }

  function renderInto(el, kit, size, opts) {
    if (!el) return;
    opts = opts || {}; opts.size = size || 132;
    el.innerHTML = svg(kit, opts);
  }

  // ---- archetype -> kit ---------------------------------------------------
  // The quiz's answer finally dresses the athlete. Only ever sets kit pieces the
  // member has unlocked, and never touches identity (skin / build / hair / face)
  // — nor stance, which is how a member chooses to carry themselves, not
  // something a quiz result gets to decide.
  var TIER_FRAME = { COMMON: 'ring', RARE: 'lime', ELITE: 'grad', LEGENDARY: 'grad', MYTHIC: 'holo' };
  var SPORT_ALIAS = { run: 'running', walk: 'running', trail: 'running', cycle: 'cycling', tri: 'triathlon',
    swim: 'swimming', hyrox: 'hyrox', strength: 'strength', crossfit: 'strength', climb: 'climbing',
    yoga: 'yoga', boxing: 'none', football: 'football', basketball: 'basketball', netball: 'netball',
    rugby: 'rugby', tennis: 'tennis', badminton: 'badminton', tabletennis: 'tabletennis', padel: 'padel' };
  function archetypeKit(kit, arch, earnedIds) {
    var k = norm(kit); if (!arch) return k;
    var earned = {}; (earnedIds || []).forEach(function (id) { earned[id] = true; });
    var sport = SPORT_ALIAS[String(arch.sport || '').toLowerCase()];
    if (sport && has('sport', sport)) k.sport = sport;
    var f = TIER_FRAME[String(arch.tier || '').toUpperCase()];
    if (f) { var lk = lockOf('frame', f); if (!lk || earned[lk]) k.frame = f; }
    // match the top colour to the sport accent so the whole figure reads as one kit
    if (sport) {
      var accent = (sportOf(sport).accent || '').toLowerCase();
      for (var i = 0; i < CLOTH.length; i++) if (CLOTH[i].toLowerCase() === accent) { k.topColor = String(i); break; }
    }
    return k;
  }

  // ---- builder UI ---------------------------------------------------------
  function injectCSS() {
    if (document.getElementById('hub-av-css')) return;
    var s = document.createElement('style'); s.id = 'hub-av-css';
    s.textContent =
      '.hub-avb{display:grid;grid-template-columns:214px 1fr;gap:1.3rem;align-items:start}' +
      '.hub-avb__stage{position:sticky;top:90px;background:linear-gradient(180deg,#161c25,#0e1218);border:1px solid var(--line,#2a2f36);border-radius:18px;padding:14px;text-align:center}' +
      '.hub-avb__fig{min-height:250px;display:flex;align-items:center;justify-content:center}' +
      '.hub-avb__fig svg{max-height:250px;height:250px;width:auto;transition:transform .18s cubic-bezier(.2,1.4,.4,1)}' +
      '.hub-avb__fig.is-pop svg{transform:scale(1.06)}' +
      '.hub-avb__tools{display:flex;flex-wrap:wrap;gap:6px;justify-content:center;margin-top:10px}' +
      '.hub-avb__tool{background:none;border:1px solid var(--line,#2a2f36);color:var(--muted,#9aa);font:600 .58rem/1 var(--font-mono,monospace);letter-spacing:.08em;text-transform:uppercase;border-radius:999px;padding:.55em .8em;cursor:pointer;transition:.15s}' +
      '.hub-avb__tool:hover,.hub-avb__tool.is-on{color:var(--lime,#B7FF00);border-color:var(--lime,#B7FF00)}' +
      // Loadouts. Sit directly under the figure because that is what they are: the same athlete,
      // three looks. Deliberately NOT chips in the right-hand column — everything over there
      // changes one part, and these change all of them at once.
      '.hub-avb__loadouts{margin-top:12px;border-top:1px solid var(--line,#2a2f36);padding-top:10px}' +
      '.hub-avb__ldlbl{display:flex;align-items:center;justify-content:space-between;gap:8px;font:600 .6rem/1 var(--font-mono,monospace);letter-spacing:.1em;text-transform:uppercase;color:var(--muted,#9aa);margin-bottom:7px}' +
      '.hub-avb__slots{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}' +
      // The clear "×" is a sibling of the slot button, never a child: a <button> inside a
      // <button> is invalid markup and browsers recover from it by dropping one of them.
      '.hub-avb__slotcell{position:relative;display:flex}' +
      '.hub-avb__slot{width:100%;background:none;border:1px dashed var(--line,#2a2f36);border-radius:12px;padding:4px;cursor:pointer;transition:.15s;min-height:54px;display:flex;align-items:center;justify-content:center;color:var(--muted,#9aa);font:600 .55rem/1.2 var(--font-mono,monospace);letter-spacing:.06em;text-transform:uppercase}' +
      '.hub-avb__slot.is-filled{border-style:solid}' +
      '.hub-avb__slot:hover,.hub-avb__slot:focus-visible{border-color:var(--lime,#B7FF00);color:var(--lime,#B7FF00)}' +
      '.hub-avb__slot.is-worn{border-color:var(--lime,#B7FF00);box-shadow:inset 0 0 0 1px var(--lime,#B7FF00)}' +
      // Save mode: every slot becomes a target, including the filled ones being overwritten.
      '.hub-avb__slots.is-saving .hub-avb__slot{border-color:var(--lime,#B7FF00);border-style:dashed;animation:hubAvbPulse 1.1s ease-in-out infinite}' +
      '@keyframes hubAvbPulse{0%,100%{opacity:1}50%{opacity:.62}}' +
      '@media(prefers-reduced-motion:reduce){.hub-avb__slots.is-saving .hub-avb__slot{animation:none}}' +
      '.hub-avb__slotx{position:absolute;top:-6px;right:-6px;width:18px;height:18px;border-radius:50%;border:1px solid var(--line,#2a2f36);background:#0e1218;color:var(--muted,#9aa);font:700 .6rem/1 var(--font-mono,monospace);cursor:pointer;padding:0}' +
      '.hub-avb__slotx:hover{color:var(--lime,#B7FF00);border-color:var(--lime,#B7FF00)}' +
      '.hub-avb__tabs{display:flex;gap:6px;margin-bottom:14px;border-bottom:1px solid var(--line,#2a2f36)}' +
      '.hub-avb__tab{background:none;border:0;border-bottom:2px solid transparent;color:var(--muted,#9aa);font:600 .72rem/1 inherit;letter-spacing:.04em;padding:.7em 1em;cursor:pointer;transition:.15s}' +
      '.hub-avb__tab:hover{color:var(--fg,#eee)}' +
      '.hub-avb__tab.is-on{color:var(--lime,#B7FF00);border-bottom-color:var(--lime,#B7FF00)}' +
      '.hub-avb__cat{margin-bottom:15px}' +
      '.hub-avb__lbl{font:600 .6rem/1 var(--font-mono,monospace);letter-spacing:.1em;text-transform:uppercase;color:var(--muted,#9aa);margin-bottom:7px}' +
      // Scrollable option rows. `overscroll-behavior:contain` stops the page from
      // scrolling once a row hits its end; the matching `data-lenis-prevent` attribute
      // is set in buildRows() because Lenis (smoothWheel) otherwise swallows the wheel
      // and touch events entirely and the row cannot be scrolled at all.
      // Fixed px cap, deliberately NOT a vh/dvh value: a viewport-relative max-height
      // collapses the whole row to 0 wherever the viewport reports no height (headless
      // panes, iOS dynamic-viewport transitions), which hides every option.
      '.hub-avb__opts{display:flex;flex-wrap:wrap;gap:6px;max-height:326px;min-height:44px;overflow-y:auto;overscroll-behavior:contain;padding:2px 4px 2px 2px;scrollbar-width:thin;scrollbar-color:#3a424d transparent}' +
      '.hub-avb__opts::-webkit-scrollbar{width:8px}' +
      '.hub-avb__opts::-webkit-scrollbar-track{background:transparent}' +
      '.hub-avb__opts::-webkit-scrollbar-thumb{background:#3a424d;border-radius:99px}' +
      '.hub-avb__opts::-webkit-scrollbar-thumb:hover{background:var(--lime,#B7FF00)}' +
      // Only the rows that actually overflow get the "there is more" affordance.
      '.hub-avb__cat.is-scroll{position:relative}' +
      '.hub-avb__cat.is-scroll::after{content:"";position:absolute;left:0;right:10px;bottom:0;height:34px;pointer-events:none;background:linear-gradient(to top,var(--carbon,#0b0e12),transparent)}' +
      '.hub-avb__cat.is-scroll.is-end::after{opacity:0;transition:opacity .2s}' +
      '.hub-avb__more{float:right;font:600 .55rem/1 var(--font-mono,monospace);letter-spacing:.08em;text-transform:uppercase;color:var(--muted,#9aa)}' +
      '.hub-avb__o{position:relative;min-width:42px;height:36px;padding:0 10px;border-radius:10px;border:1px solid var(--line,#2a2f36);background:var(--carbon,#0b0e12);color:var(--fg,#eee);font-size:.74rem;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:.15s}' +
      '.hub-avb__o:hover{border-color:#4a525c}' +
      '.hub-avb__o.sw{width:36px;min-width:36px;padding:0}' +
      '.hub-avb__o.pic{width:60px;height:60px;padding:0;overflow:hidden}' +
      '.hub-avb__o.pic svg{width:100%;height:100%}' +
      // A stance can only be judged full-body, so its chips are portrait, not square.
      '.hub-avb__o.fig{width:46px;height:74px;padding:0;overflow:hidden}' +
      '.hub-avb__o.fig svg{width:100%;height:100%}' +
      '.hub-avb__o.is-on{border-color:var(--lime,#B7FF00);box-shadow:0 0 0 1px var(--lime,#B7FF00)}' +
      '.hub-avb__o.is-locked{opacity:.4;cursor:pointer}' +
      '.hub-avb__o .lk{position:absolute;top:-5px;right:-5px;font-size:.66rem;opacity:1}' +
      '.hub-avb__hint{min-height:1.1em;font-size:.72rem;color:var(--lime,#B7FF00);margin:-6px 0 10px}' +
      '.hub-avb__hint.is-lock{color:#ffb84d}' +
      '@media(max-width:640px){.hub-avb{grid-template-columns:1fr}.hub-avb__stage{position:static;max-width:260px;margin:0 auto}.hub-avb__opts{max-height:258px}}' +
      '@media(prefers-reduced-motion:reduce){.hub-avb__fig svg{transition:none}.hub-avb__fig.is-pop svg{transform:none}}';
    document.head.appendChild(s);
  }

  // Three groups instead of one flat scroll: who you are, what you wear, extras.
  // Stance sits in Athlete, not Kit: it is how you carry yourself, not something
  // you put on. Prop sits in Kit, directly under the sport that supplies it.
  var TABS = [
    { id: 'athlete', label: 'Athlete', rows: [['build', 'Build', 'text'], ['stance', 'Stance', 'fig'], ['skin', 'Skin', 'sw'], ['hair', 'Hair', 'pic'], ['hairColor', 'Hair colour', 'sw'], ['face', 'Expression', 'pic'], ['beard', 'Facial hair', 'pic'], ['glasses', 'Eyewear', 'pic']] },
    { id: 'kit',     label: 'Kit',     rows: [['sport', 'Sport kit', 'text'], ['prop', 'In hand', 'text'], ['top', 'Top', 'text'], ['topColor', 'Top colour', 'sw'], ['bottom', 'Bottoms', 'text'], ['bottomColor', 'Bottoms colour', 'sw'], ['shoe', 'Shoes', 'sw']] },
    { id: 'extras',  label: 'Extras',  rows: [['accessory', 'Accessory', 'text'], ['frame', 'Frame', 'text'],
                                              ['frameColor', 'Border colour', 'sw'], ['plateColor', 'Card background', 'sw'],
                                              ['rimColor', 'Athlete outline', 'sw']] }
  ];
  // "Reroll kit" must not reroll identity — randomising someone's skin tone or
  // build is a different act from randomising their outfit. Stance rides with
  // the kit: it is expression, not identity.
  var KIT_CATS = ['sport', 'prop', 'top', 'topColor', 'bottom', 'bottomColor', 'shoe', 'accessory', 'frame', 'stance', 'frameColor', 'plateColor', 'rimColor'];
  var ALL_CATS = ['build', 'skin', 'hair', 'hairColor', 'face', 'beard', 'glasses'].concat(KIT_CATS);

  function builder(host, cfg) {
    injectCSS();
    cfg = cfg || {};
    var kit = norm(cfg.kit);
    var earned = {}; (cfg.earnedIds || []).forEach(function (id) { earned[id] = true; });
    var badges = {}; (cfg.badges || []).forEach(function (b) { if (b && b.id) badges[b.id] = b; });
    var activeTab = 'athlete', headOnly = false;

    host.textContent = '';
    var wrap = el('div', 'hub-avb');
    var stage = el('div', 'hub-avb__stage');
    var fig = el('div', 'hub-avb__fig');
    var tools = el('div', 'hub-avb__tools');
    var tView = btn('hub-avb__tool', 'Head'), tKit = btn('hub-avb__tool', '🎲 Kit'), tAll = btn('hub-avb__tool', '🎲 All');
    tools.appendChild(tView); tools.appendChild(tKit); tools.appendChild(tAll);
    stage.appendChild(fig); stage.appendChild(tools);

    // ---- saved looks -------------------------------------------------------------------
    var slots = normLoadouts(cfg.loadouts);
    var saving = false;
    var ld = el('div', 'hub-avb__loadouts');
    var ldLbl = el('div', 'hub-avb__ldlbl');
    var ldTitle = el('span'); ldTitle.textContent = 'Saved looks';
    var ldSave = btn('hub-avb__tool', 'Save look');
    ldLbl.appendChild(ldTitle); ldLbl.appendChild(ldSave);
    var slotRow = el('div', 'hub-avb__slots');
    ld.appendChild(ldLbl); ld.appendChild(slotRow);
    stage.appendChild(ld);

    var right = el('div', 'hub-avb__cats');
    var tabBar = el('div', 'hub-avb__tabs');
    var hint = el('div', 'hub-avb__hint');
    var body = el('div');
    right.appendChild(tabBar); right.appendChild(hint); right.appendChild(body);
    wrap.appendChild(stage); wrap.appendChild(right); host.appendChild(wrap);

    function el(t, c) { var e = document.createElement(t); if (c) e.className = c; return e; }
    function btn(c, txt) { var b = document.createElement('button'); b.type = 'button'; b.className = c; b.textContent = txt; return b; }

    function draw(pop) {
      fig.innerHTML = svg(kit, { size: headOnly ? 230 : 250, crop: headOnly ? 'head' : null, bg: cfg.bg, architect: cfg.architect });
      if (pop) { fig.classList.add('is-pop'); setTimeout(function () { fig.classList.remove('is-pop'); }, 190); }
      if (cfg.onChange) cfg.onChange(assign({}, kit));
    }
    function say(msg, isLock) {
      hint.textContent = msg || '';
      hint.className = 'hub-avb__hint' + (isLock ? ' is-lock' : '');
    }
    // Asks the renderer where the item actually went rather than re-deriving it,
    // so the sentence a member reads can never drift from what they are looking
    // at. Only one reason lands here now — hands full — since the victory stance
    // raises everything.
    // Said out loud the moment it happens, rather than left for the member to
    // wonder why their athlete went flat.
    function borderNote() {
      var plate = pickColor('plateColor', kit.plateColor, cfg.bg || DEFAULT_BG);
      var rim = pickColor('rimColor', kit.rimColor, RIM_COLOR);
      if (!lowContrast(plate, rim)) return false;
      say('That outline is the same value as your card background — your athlete will sink into it.', true);
      return true;
    }
    function propNote() {
      var rp = resolveProp(kit);
      if (!rp || rp.mode !== 'ground') return false;
      say('Both hands are busy in ' + labelOf('stance', kit.stance).toLowerCase() +
          ' — your ' + rp.p.label.toLowerCase() + ' is set down at your feet.');
      return true;
    }

    // Two looks are "the same" when every part matches. Compared through norm() so a slot saved
    // by an older version, with fields this one fills in by default, still lights up as worn.
    function sameKit(a, b) {
      var x = norm(a), y = norm(b), k;
      for (k in x) if (x.hasOwnProperty(k) && x[k] !== y[k]) return false;
      for (k in y) if (y.hasOwnProperty(k) && x[k] !== y[k]) return false;
      return true;
    }
    function persistLoadouts() {
      if (cfg.onLoadouts) {
        try { cfg.onLoadouts(slots.map(function (s) { return s ? { kit: assign({}, s.kit) } : null; })); }
        catch (e) {}
      }
    }
    function setSaveMode(on) {
      saving = !!on;
      ldSave.textContent = saving ? 'Cancel' : 'Save look';
      ldSave.classList.toggle('is-on', saving);
      buildSlots();
      say(saving ? 'Pick a slot to save this look into.' : '');
    }
    function buildSlots() {
      slotRow.textContent = '';
      slotRow.classList.toggle('is-saving', saving);
      slots.forEach(function (s, i) {
        var num = i + 1;
        var cell = el('div', 'hub-avb__slotcell');
        var b = btn('hub-avb__slot' + (s ? ' is-filled' : ''), '');
        if (s) {
          b.innerHTML = svg(s.kit, { size: 46, crop: 'head' });
          // Worn state is only meaningful outside save mode — in save mode every slot is a
          // target, and highlighting one as "current" reads as "this is the one".
          if (!saving && sameKit(s.kit, kit)) b.classList.add('is-worn');
        } else {
          b.textContent = saving ? 'Save here' : 'Look ' + num;
        }
        var label = s
          ? (saving ? 'Replace saved look ' + num + ' with what you are wearing' : 'Wear saved look ' + num)
          : 'Save this look as look ' + num;
        b.setAttribute('aria-label', label); b.title = label;
        b.addEventListener('click', function () {
          if (saving || !s) {
            slots = loadoutSet(slots, i, kit);
            setSaveMode(false);
            persistLoadouts();
            buildSlots();
            say('Saved as look ' + num + '. It stays on your profile.');
            track('avatar_loadout_save', { slot: num });
          } else {
            kit = norm(s.kit);
            buildRows(); buildSlots(); draw(true);
            say('Wearing look ' + num + '.');
            track('avatar_loadout_wear', { slot: num });
          }
        });
        cell.appendChild(b);
        // Clearing is only offered in save mode, so a mis-tap while browsing looks can never
        // delete one. It is a sibling button, never nested inside the slot button.
        if (s && saving) {
          var x = btn('hub-avb__slotx', '×');
          x.setAttribute('aria-label', 'Clear saved look ' + num);
          x.title = 'Clear saved look ' + num;
          x.addEventListener('click', function (e) {
            e.stopPropagation();
            slots = loadoutSet(slots, i, null);
            setSaveMode(false);
            persistLoadouts();
            buildSlots();
            say('Cleared look ' + num + '.');
          });
          cell.appendChild(x);
        }
        slotRow.appendChild(cell);
      });
    }
    function track(name, params) {
      try { if (window.HubProfile && window.HubProfile.track) window.HubProfile.track(name, params); } catch (e) {}
    }
    ldSave.addEventListener('click', function () { setSaveMode(!saving); });
    // Escape leaves save mode rather than stranding a member in a state where every slot is a
    // destructive target and the only way out is a button labelled Cancel.
    host.addEventListener('keydown', function (e) { if (e.key === 'Escape' && saving) { setSaveMode(false); e.stopPropagation(); } });

    function buildTabs() {
      tabBar.textContent = '';
      TABS.forEach(function (t) {
        var b = btn('hub-avb__tab' + (t.id === activeTab ? ' is-on' : ''), t.label);
        b.addEventListener('click', function () { activeTab = t.id; buildTabs(); buildRows(); });
        tabBar.appendChild(b);
      });
    }

    function buildRows() {
      body.textContent = '';
      var tab = TABS.filter(function (t) { return t.id === activeTab; })[0];
      if (activeTab === 'athlete') body.appendChild(presetRow());
      tab.rows.forEach(function (row) {
        var cat = row[0], label = row[1], kind = row[2];
        if (!parts[cat] || !parts[cat].length) return;
        var box = el('div', 'hub-avb__cat');
        var l = el('div', 'hub-avb__lbl'); l.textContent = label; box.appendChild(l);
        var opts = scroller();
        parts[cat].forEach(function (opt) { opts.appendChild(chip(cat, opt, kind)); });
        box.appendChild(opts); body.appendChild(box);
        markOverflow(box, opts, l);
      });
    }

    // Lenis runs with smoothWheel:true and swallows wheel/touch inside nested
    // scrollers unless the element carries data-lenis-prevent. Without it the hair
    // row (34 items) simply could not be scrolled — the rest was unreachable.
    function scroller() {
      var d = el('div', 'hub-avb__opts');
      d.setAttribute('data-lenis-prevent', '');
      d.tabIndex = 0;                       // keyboard users can scroll it too
      return d;
    }

    // Only flag rows that genuinely overflow, so short rows get no fade and no
    // "scroll for more" label they don't need.
    function markOverflow(box, opts, labelEl) {
      var marked = false;
      function check() {
        if (marked || opts.scrollHeight - opts.clientHeight < 8) return;
        marked = true;
        box.classList.add('is-scroll');
        var more = el('span', 'hub-avb__more');
        more.textContent = 'scroll for more';
        labelEl.appendChild(more);
        opts.addEventListener('scroll', function () {
          box.classList.toggle('is-end', opts.scrollTop + opts.clientHeight >= opts.scrollHeight - 4);
        }, { passive: true });
      }
      // Measure NOW — the row is already in the document, so reading scrollHeight
      // forces layout and gives a real answer. rAF alone was not enough: it does not
      // fire in a backgrounded/hidden tab, so the affordance silently never appeared.
      check();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(check);
      window.addEventListener('resize', check, { passive: true });
    }

    function presetRow() {
      var box = el('div', 'hub-avb__cat');
      var l = el('div', 'hub-avb__lbl'); l.textContent = 'Starter looks'; box.appendChild(l);
      var row = scroller();
      PRESETS.forEach(function (p) {
        var b = btn('hub-avb__o pic', ''); b.title = p.name;
        b.innerHTML = svg(p.kit, { size: 58, crop: 'head' });
        b.addEventListener('click', function () { assign(kit, p.kit); buildRows(); draw(true); say('Starter look: ' + p.name); });
        row.appendChild(b);
      });
      box.appendChild(row); return box;
    }

    function chip(cat, opt, kind) {
      var b = btn('hub-avb__o' + (kind === 'sw' ? ' sw' : kind === 'pic' ? ' pic' : kind === 'fig' ? ' fig' : ''), '');
      var locked = opt.lock && !earned[opt.lock];
      if (kind === 'sw') { b.style.background = opt.color; b.title = opt.label || ''; }
      else if (kind === 'pic' || kind === 'fig') {
        // A thumbnail of THIS member wearing THIS part — 34 hairstyles as a text
        // list would be unusable, and you cannot choose a face, or a pose, from its name.
        var preview = assign(assign({}, kit), (function () { var o = {}; o[cat] = opt.id; return o; })());
        b.innerHTML = kind === 'fig'
          ? svg(preview, { size: 72 })
          : svg(preview, { size: 58, crop: 'head' });
        b.title = opt.label || '';
      } else { b.textContent = opt.label; }
      if (kit[cat] === opt.id) b.classList.add('is-on');
      if (locked) {
        b.classList.add('is-locked');
        var bd = badges[opt.lock];
        // v1 showed the raw achievement id here ("Unlock by earning: mythic").
        b.title = bd ? ('Locked — earn "' + bd.title + '": ' + bd.desc) : 'Locked';
        var lk = el('span', 'lk'); lk.textContent = bd && bd.icon ? bd.icon : '🔒'; b.appendChild(lk);
      }
      b.addEventListener('click', function () {
        if (locked) {
          var bd2 = badges[opt.lock];
          say(bd2 ? ('🔒 ' + (opt.label || 'This') + ' unlocks with "' + bd2.title + '" — ' + bd2.desc)
                  : ('🔒 ' + (opt.label || 'This') + ' is still locked'), true);
          return;
        }
        kit[cat] = opt.id;
        // Stance and prop chips are previews of each other, so changing either
        // has to redraw the whole row set rather than just re-ticking one chip.
        if (kind === 'pic' || kind === 'fig' || cat === 'sport' || cat === 'prop') { buildRows(); } else {
          var sibs = b.parentNode.children;
          for (var i = 0; i < sibs.length; i++) sibs[i].classList.remove('is-on');
          b.classList.add('is-on');
        }
        draw(true);
        if (!borderNote() && !propNote()) say('');
        try {
          if (window.HubProfile && window.HubProfile.track)
            window.HubProfile.track('avatar_equip', { category: cat, item: opt.id, label: (opt.label || opt.color || opt.id) });
        } catch (e) {}
      });
      return b;
    }

    function reroll(cats) {
      cats.forEach(function (cat) {
        var pool = (parts[cat] || []).filter(function (o) { return !(o.lock && !earned[o.lock]); });
        if (pool.length) kit[cat] = pool[Math.floor(Math.random() * pool.length)].id;
      });
      buildRows(); draw(true);
    }
    tKit.addEventListener('click', function () { reroll(KIT_CATS); say('Rerolled the kit — your athlete is untouched.'); });
    tAll.addEventListener('click', function () { reroll(ALL_CATS); say('Rerolled everything.'); });
    tView.addEventListener('click', function () {
      headOnly = !headOnly;
      tView.textContent = headOnly ? 'Full body' : 'Head';
      tView.classList.toggle('is-on', headOnly);
      draw(false);
    });

    buildTabs(); buildRows(); buildSlots(); draw(false);
    return {
      get: function () { return assign({}, kit); },
      // buildSlots() too, or the "worn" ring keeps pointing at the look they were wearing
      // before the caller changed the kit underneath us.
      set: function (k) { kit = norm(k); buildRows(); buildSlots(); draw(false); },
      getLoadouts: function () { return slots.map(function (s) { return s ? { kit: assign({}, s.kit) } : null; }); }
    };
  }

  window.HubAvatar = {
    parts: parts, sports: SPORTS, builds: BUILDS, stances: STANCES, props: PROPS, builder: builder,
    defaultKit: defaultKit, presets: presets, presetFor: presetFor, norm: norm, migrate: migrate,
    normLoadouts: normLoadouts, loadoutSet: loadoutSet, LOADOUT_SLOTS: LOADOUT_SLOTS,
    svg: svg, silhouette: silhouette, SIL_FLOOR: SIL_FLOOR, renderInto: renderInto,
    lockOf: lockOf, labelOf: labelOf, archetypeKit: archetypeKit, initPeeps: initPeeps,
    STANCE_HANDS_BUSY: STANCE_HANDS_BUSY, resolveProp: resolveProp, pickColor: pickColor, lowContrast: lowContrast, _skin: SKIN, _cloth: CLOTH
  };
  initPeeps();
})();
